import amplitudeLog from '@client/amplitude';
import {
  COVER_MAP_SNAPSHOT_BACKGROUND,
  snapshotCanvasOntoBackground,
} from '@client/helpers/canvas-snapshot.helpers';
import { AMP_DESIGN_MOVE_BUILDING_POINT } from '@shared/constants';
import mapboxgl, { Map, MapMouseEvent, MapOptions } from 'mapbox-gl';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  takeUntil,
  pairwise,
  filter,
  combineLatest,
} from 'rxjs';
import {
  DEFAULT_MAP_FOV,
  ISOMETRIC_MAP_FOV,
  mapConfigDefaults,
  applyCanvasOutlineRemoval,
  MapLayerIds,
} from './helpers/map-config.constants';
import {
  loadMapIcons,
  waitForMapLoad,
  waitForStyleLoad,
} from './helpers/map.helpers';
import { bindDisableImportedLineProgressEvaluation } from './helpers/map-basemap-line-progress.helpers';
import {
  resolveMovedGeometryCommit,
  type RebaseMapOriginResult,
} from './helpers/location-origin-rebase.helpers';
import type { StoreysCommitReason } from '@shared/helpers/project-events/design-commit-events.helpers';
import type { LocationChangeReason } from './models/location-change.model';
import {
  applyLocationPinOverlayFlags,
  resolveLocationPinOverlayFlags,
} from './helpers/location-pin-visibility.helpers';
import { applyImportedBasemapVisibility } from './map-renderer/mask.helpers';
import {
  getFacadeSettleProgress,
  getQuickCameraAnimationProgress,
  getMapFovForPitch,
  getShiftTPitchTarget,
  QUICK_CAMERA_ANIMATION_DURATION_MS,
  setMapPerspectiveMode,
} from './helpers/map-perspective.helpers';
import {
  DraftShape,
  Shape,
  Storey,
  STOREY_MIN_HEIGHT,
} from '@shared/models/design/storey.model';
import { Building } from '@shared/models/design/building-design.model';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { getId } from '@shared/helpers/object.helpers';
import {
  createDesignBuilding,
  getStoreyBelow,
  getStoreyById,
  getStoreyElevationRange,
  getStoreyIndex,
  getStoreys,
  mergeNearbyPointsInStoreys,
} from '@shared/helpers/design/storey.helpers';
import {
  applyCompletedDraftToStoreys,
  applyCompletedDraftsToStoreys,
} from '@shared/helpers/design/draft-shape.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import { createDraftShape } from '@shared/helpers/design/factories';
import {
  createBuildingFootprintTemplateDrafts,
  type BuildingFootprintTemplateGeometry,
} from './helpers/building-footprint-template.helpers';
import { mutateShape } from '@shared/helpers/design/mutators/shape.mutator';
import {
  collectTransitiveLinkedCutoutsAbove,
  getIntersectingShapesByStorey,
  isPointInsideBuildingSolids,
  isPointInsideStoreyCutout,
  isPointInsideStoreyShapes,
  isPointInsideStoreySolidOccupancy,
} from '@shared/helpers/design/shape.helpers';
import { shouldYieldDeleteToBlueprintUnderlay } from './helpers/blueprint-underlay-keyboard.helpers';
import { isDesignSnapshotSupersededError } from './helpers/design-snapshot-persist.helpers';
import { shouldRemoveEntireShapeOnVertexDelete } from './helpers/shape-vertex-delete.helpers';
import { resolveNewDraftCutoutMode } from './helpers/draft-cutout-intent.helpers';
import {
  buildPointMoves,
  shouldCollapseLineSegmentDrag,
  type PointMove,
} from './helpers/point-move-propagation.helpers';
import { Coordinate } from '@shared/models/geography.model';
import {
  DraftClickPayload,
  DraftPreviewPayload,
  MapGeometryMeasurementFocus,
  MapHoverState,
  MapTooltipPayload,
  type MapTooltipCornerAngleRef,
  type MapTooltipSegmentRef,
  MapStateObservables,
  PointPayload,
} from './models/map-observables.models';
import { MapEventHandler } from './map-event-handler/';
import { MapRenderer } from './map-renderer/map-renderer';
import { MapThreeJs } from './threejs';
import type { ExtrusionRenderParams } from './threejs/extrusion-render-params.helpers';
import type {
  ShapeRemoveEvent,
  ShapeTranslateEvent,
  Handlers,
  PointRemoveEvent,
  PointRemoveAtEvent,
} from './map-event-handler/vertex-interaction.helpers';
import { mutateStoreys } from '@shared/helpers/design/mutators/storeys.mutator';
import { mutateStoreyInDesign } from '@shared/helpers/design/mutators/design-storey.mutator';
import {
  convertCoordinateToMetricOffset,
  convertMetricOffsetToCoordinate,
} from '@shared/helpers/geometry/coordinates.helpers';
import { isPolygonSelfIntersecting } from '@shared/helpers/geometry/polygon.helpers';
import { dragIncreasesPointSeparation } from '@shared/helpers/geometry/point-separation.helpers';
import {
  getManualSegmentLengthTimestamps,
  recordManualSegmentLengthEdit,
} from './helpers/segment-length-edit-history.helpers';
import {
  getManualCornerAngleTimestamps,
  recordManualCornerAngleEdit,
} from './helpers/corner-angle-edit-history.helpers';
import {
  getBuildingRotationCenter,
  rotatePolygonAroundCenter,
} from './helpers/building-rotation.helpers';

import type {
  PendingPointDeletion,
  PointActionContext,
} from './map-event-handler/vertex-interaction.helpers';
import {
  getNextPointIndexAfterRemoval,
  shouldIgnoreQuickPointDrag,
  shouldIgnoreInsertPoint,
  tryRemovePointOnRelease,
} from './map-event-handler/vertex-interaction.helpers';
import { SnapAndBoundaries } from './snap';
import {
  clampMezzanineStoreysInStorey,
  insertMezzanineBoundaryPoints,
} from '../../../shared/helpers/design/mezzanine-helpers';
import { Point2D, Polygon2D } from '@shared/models/geometry.model';
import {
  CUTOUT_MIN_MARGIN_METERS,
  getCutoutsEnclosedBySolid,
  getShapesContainedInCutout,
  getSolidShapes,
  isCutoutInsideStoreyWithMargin,
  resolveClampedCutoutVertexPosition,
} from '@shared/helpers/design/cutout.helpers';
import { getItemById } from '@shared/helpers/array.helpers';
import { enqueueAsync, sleep } from '@shared/helpers/promise.helpers';
import {
  getLargestStoreySegment,
  planCommittedSegmentResize,
  type StoreySegmentReference,
} from './helpers/segment-length-label.helpers';
import {
  getPolygonCornerAngleDegrees,
  planCornerAngleEdit,
  planReleasedCornerRightAngle,
  planReleasedCornerRightAngles,
  planEligibleCornerRightAngles,
  resolveSelectedCornerRightAngleTargets,
} from './helpers/corner-angle-edit.helpers';
import {
  shouldApplyDraftAfterAwait,
  shouldClearDraftOnBuildingSelect,
  shouldExitDrawToolOnEscape,
} from './helpers/map-observables.helpers';
import {
  DESIGN_CANVAS_PLATE_COLOR,
  MAPBOX_BASEMAP_MASK_LAYER_ID,
  MAPBOX_BASEMAP_MASK_SOURCE_ID,
} from './map.constants';
import {
  BlueprintUnderlayController,
  type BlueprintCalibrateLabel,
  type BlueprintEscapeResult,
  type BlueprintInteractionMode,
} from './blueprint-underlay/blueprint-underlay-controller';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import {
  getBlueprintImageCoordinates,
  getBlueprintUnderlayDisplayElevationMeters,
  translateBlueprintUnderlay,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import type { ViewMode } from './components/ViewModeToggle';
import { MiddleMousePanController } from './middle-mouse-pan.controller';
import { MouseNavigationController } from './mouse-navigation.controller';
import {
  getBearingSnapDirectionsForSelection,
  getNextBuildingBearingAlignment,
  getRankedBearingDirectionsForSelection,
} from './helpers/building-bearing-snap.helpers';
import type { DesignMapCameraState, DesignMapTool } from './store/design.store';
import {
  consumeDesignMapLocationFlyToSuppression,
  registerDesignDraftDiscard,
} from './store/design.store';
import {
  getBuildingElevationCenterOffset,
  getBuildingFocusCamera,
  getBuildingTargetPitchCenterOffset,
  getBuildingVolumeFocusCamera,
  getMapViewportLeftInset,
} from './helpers/building-viewport.helpers';
import { allowsDesignSpacePan } from './helpers/design-canvas-toolbar.helpers';
import { setLayerVisibilityIfChanged } from './helpers/map-layer-order.helpers';
import {
  clearBasemapCoverSlot,
  getBasemapDimMaskOpacity,
  placeBasemapCoverAboveImports,
} from './helpers/map-basemap-cover.helpers';
import {
  MEASURE_ISOLATION_LAYER_IDS,
  captureMeasureIsolationVisibility,
  type MeasureIsolationVisibility,
} from './helpers/blueprint-measure-isolation.helpers';

/**
 * DesignMap encapsulates Mapbox map lifecycle and exposes a minimal, React-agnostic API.
 * Instantiate once, attach to a container, subscribe to load, and destroy on unmount.
 */
const DEFAULT_SCREENSHOT_HIDDEN_LAYERS: MapLayerIds[] = [
  MapLayerIds.Points,
  MapLayerIds.DraftLine,
  MapLayerIds.DraftPoints,
];
const BUILDING_VOLUME_FOCUS_MARGIN_CYCLE = [0.2, 0.05, 0.4] as const;

type MapPaintPropertyName = Parameters<Map['setPaintProperty']>[1];
type MapPaintValue = Parameters<Map['setPaintProperty']>[2];

const SCREENSHOT_PAINT_OVERRIDES: Array<{
  id: MapLayerIds;
  properties: Partial<Record<MapPaintPropertyName, MapPaintValue>>;
}> = [
  {
    id: MapLayerIds.Line,
    properties: {
      'line-color': '#000000',
      'line-width': 3,
      'line-opacity': 1,
    },
  },
  {
    id: MapLayerIds.BuildingExtrusion,
    properties: {
      'fill-extrusion-color': '#FF3B30',
      // 'fill-extrusion-opacity': 0.,
    },
  },
];

const SCREENSHOT_FILTER_OVERRIDES: Array<{
  id: MapLayerIds;
  filter: mapboxgl.Expression | null;
}> = [
  {
    id: MapLayerIds.Line,
    filter: ['boolean', true],
  },
];

type MapScreenshotOptions = {
  hiddenLayers?: MapLayerIds[];
};

/**
 * Commits a new project location, saying why it changed.
 * Resolves with the pin that was saved when the caller knows it. That pin
 * can differ from {@link location} after the footprint is recentered.
 */
type BuildingTranslateHandler = (
  location: Coordinate,
  reason: LocationChangeReason,
) => Promise<Coordinate | void> | Coordinate | void;

type StoreysCommitHandler = (
  storeys: Storey[],
  buildingId: string | undefined,
  reason: StoreysCommitReason | undefined,
) => Promise<void> | void;

/** Snapshot captured at {@link DesignMap.commit} so a later selection cannot retarget the PUT. */
type PersistStoreysJob = {
  handler: StoreysCommitHandler;
  storeys: Storey[];
  originalStoreys: Storey[];
  buildingId: string | undefined;
  reason: StoreysCommitReason | undefined;
  epoch: number;
  revision: number;
};

type QuickPitchReturnCamera = {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
  padding: ReturnType<Map['getPadding']>;
};

export class DesignMap {
  private static activeInteractiveController: DesignMap | undefined;
  private static readonly activeDisplayControllers = new Set<DesignMap>();
  /**
   * Current working state in the map.
   */
  readonly state: MapStateObservables;

  /**
   * Latest storeys snapshot received from the React layer ({@link Map3d}) via {@link setStoreys}.
   *
   * {@link MapStateObservables.storeys$} is treated as editable/draft state; Escape should restore
   * from this snapshot (similar to reverting to the last persisted state).
   */
  private latestReceivedStoreys: Storey[] = [];

  /**
   * Snap and boundary handler for geometric snapping during interactions.
   */
  private snapHandler: SnapAndBoundaries | undefined;

  private mapInstance: Map | undefined;

  private eventHandler: MapEventHandler | undefined;
  private loaded: boolean = false;

  private renderer: MapRenderer | undefined;
  private threeJs: MapThreeJs | undefined;
  /** True while {@link setBlueprintMeasureIsolation} hides the design overlay. */
  private measureIsolationActive = false;
  /** Layer visibility captured when measure isolation turned on. */
  private measureIsolationVisibility: Record<
    string,
    MeasureIsolationVisibility
  > = {};

  private showBackgroundMap = true;
  private backgroundMapCurrentlyVisible = true;
  private showNearbyBuildings = false;
  private backgroundOverlayListenerAttached = false;
  /** When true, map is display-only ({@link MapOptions.interactive} === false at attach). */
  private isDisplayOnlyInstance = false;
  private readonly blueprintUnderlay = new BlueprintUnderlayController();
  private readonly middleMousePan = new MiddleMousePanController();
  private readonly mouseNavigation = new MouseNavigationController();
  private cameraChangeHandler:
    ((camera: DesignMapCameraState) => void) | undefined;
  private cameraMoveListenerAttached = false;
  private appliedVerticalFov: number | undefined;
  private pitchTransitionActive = false;
  /** Independent 90° orthographic facade reached by dragging past Mapbox's cap. */
  private facadeModeActive = false;
  /** Current gesture-controlled share of the extra facade projection and lens. */
  private facadeTransitionProgress = 0;
  private facadeTransitionAnimationFrame: number | undefined;
  /** Suppress the synchronous moveend emitted by a lens-only FOV update. */
  private verticalFovUpdateActive = false;
  /** Vertical screen offset used only while the exact facade projection is active. */
  private facadeVerticalOffsetPixels = 0;
  /** Camera framing to restore when Shift+T returns from top-down. */
  private quickPitchReturnCamera: QuickPitchReturnCamera | undefined;
  private buildingVolumeFocusMarginIndex = 0;
  /** Live map bearing subscriber (north compass on the view toggle). */
  private bearingChangeHandler: ((bearing: number) => void) | undefined;
  /** Last Design camera mode — soft grey below-storey guides only in 2D. */
  private perspectiveMode: ViewMode = '3d';

  private insertDragTargets:
    | { storeyId: Storey['id']; shapeId: string; pointIndex: number }[]
    | undefined;

  // Henrik: mutations happen during update; persistence only at the edge (end).
  private readonly handlers: Handlers = {
    point: {
      move: {
        start: (payload) => {
          if (payload.isDraft) return;
          this.pointMoveBaseStoreys = this.state.storeys$.getValue();
        },
        update: (payload) => {
          this.applyPointMoveUpdate(payload);
        },
        end: async (payload) => {
          const collapseShortSegment =
            this.shouldCollapseLineSegmentOnRelease(payload);
          if (payload.isLineSegmentDrag) {
            // Re-evaluate live modifiers on release before persisting geometry.
            this.applyPointMoveUpdate(payload, collapseShortSegment);
            if (collapseShortSegment)
              this.eventHandler?.deselectShapesAndPoints();
          } else {
            this.snapPointRightAngleOnRelease(payload);
          }
          // Reset drag baseline to avoid leaking state across drags.
          this.pointMoveBaseStoreys = undefined;
          if (payload.isDraft) return;
          const saved = await this.commit();
          // The move did not land. Skip the event that would count it as saved.
          if (!saved) return;
          amplitudeLog(AMP_DESIGN_MOVE_BUILDING_POINT);
        },
      },
      insert: {
        start: (payload) => {
          this.applyPointInsertStart(payload);
          // Capture baseline after insertion so drag updates move the inserted point.
          this.pointMoveBaseStoreys = this.state.storeys$.getValue();
          // Mark the new point so it renders as selected during the whole drag.
          this.selectNewlyInsertedPoint(payload);
        },
        update: (payload) => {
          this.applyPointMoveUpdate(payload); // Just do the same as point move update
        },
        end: async (payload) => {
          this.snapPointRightAngleOnRelease(payload);
          // Reset drag baseline so subsequent inserts do not reuse stale indices.
          this.pointMoveBaseStoreys = undefined;
          const ignoredInsert =
            payload.isInsertDrag &&
            shouldIgnoreInsertPoint(this.buildPointActionContext(), payload);
          if (ignoredInsert) {
            this.removeInsertDragPoints();
            // Clear selection so the stale index does not highlight an unrelated point.
            this.eventHandler?.deselectShapesAndPoints();
          }
          this.insertDragTargets = undefined;
          const saved = await this.commit();
          // Mark the newly created point as selected after a successful add-point drop.
          if (saved && payload.isInsertDrag && !ignoredInsert) {
            this.selectNewlyInsertedPoint(payload);
          }
        },
      },
      remove: async (payload: PointRemoveEvent) => {
        const enteredDraftMode = this.tryEnterDraftModeOnPointRemoval({
          shapeId: payload.shapeId,
          pointIndex: payload.pointIndex,
        });
        if (enteredDraftMode) return;
        const position = this.resolvePointPosition(
          payload.shapeId,
          payload.pointIndex,
        );
        if (!position) return;
        this.applyPointRemoveAtPosition(position);
        await this.commit();
      },
      removeAt: async (payload: PointRemoveAtEvent) => {
        const enteredDraftMode = this.tryEnterDraftModeOnPointRemoval({
          shapeId: this.eventHandler?.getSelections().shapeId,
          position: payload.position,
        });
        if (enteredDraftMode) return;
        this.applyPointRemoveAtPosition(payload.position);
        await this.commit();
      },
    },
    shape: {
      remove: async (payload: ShapeRemoveEvent) => {
        this.applyShapeRemove(payload);
        await this.commit();
      },
      translate: {
        end: async (payload: ShapeTranslateEvent) => {
          if (payload.duplicateBuilding) {
            if (!this.onDuplicateBuilding) return;
            this.locationDragCommitPending = true;
            await this.onDuplicateBuilding?.(
              this.storeysBuildingId,
              payload.delta,
            );
            return;
          }
          this.applyShapeTranslate(payload);
          this.clampMezzanineStoreysBeforeCommit();
          this.mergeNearbyPointsBeforeCommit();
          // Persist the translated storeys; rebase the pin locally so save
          // cannot apply origin and points on separate frames.
          const underlay = this.blueprintUnderlay.getUnderlay();
          const moveCommit = resolveMovedGeometryCommit({
            location: this.state.location$.getValue(),
            selectedBuildingId: this.storeysBuildingId,
            storeys: this.state.storeys$.getValue(),
            otherBuildings: this.state.otherBuildings$.getValue(),
            blueprintUnderlays: underlay ? [underlay] : undefined,
          });
          this.applyRebasedOrigin(moveCommit.display);
          const movedBuilding =
            this.designMapTool === 'moveBuilding' ||
            this.state.isBuildingOutlineSelected$.getValue();
          await this.commit({
            storeys: moveCommit.persistStoreys,
            reason: movedBuilding ? 'building-move' : undefined,
          });
        },
      },
    },
    abort: () => {
      this.pointMoveBaseStoreys = undefined;
      this.insertDragTargets = undefined;
      this.reset();
    },
  };

  private onCommit: StoreysCommitHandler | undefined;
  private onDuplicateBuilding:
    | ((buildingId: string | undefined, delta: Point2D) => Promise<void>)
    | undefined;
  /** Building that currently owns {@link MapStateObservables.storeys$} (DEV-2576). */
  private storeysBuildingId: string | undefined;

  private onBuildingTranslate: BuildingTranslateHandler | undefined;
  private onStoreyNavigate: ((direction: 'tab' | number) => void) | undefined;
  private onStoreySelect: ((storeyId: Storey['id']) => void) | undefined;
  /** After a footprint draft completes successfully — UI returns to Select. */
  private onDraftComplete: (() => void) | undefined;
  private onBlueprintSelectRequest: (() => void) | undefined;
  private onBlueprintDeselectRequest: (() => void) | undefined;
  private onBlueprintContextMenuRequest:
    ((position: { top: number; left: number }) => void) | undefined;
  private designMapTool: DesignMapTool = 'build';
  private buildingFootprintTemplate:
    BuildingFootprintTemplateGeometry | undefined;
  private preserveTemplatePlacementViewport = false;
  private customBuildingFootprintDrawingActive = false;
  /** Space held for temporary pan (cursor only — never a tool chip). */
  private spacePanActive = false;
  private spacePanListenersAttached = false;
  private onCreateBuilding:
    ((points: Point2D[]) => Promise<void> | void) | undefined;
  private onSelectBuilding: ((buildingId: string) => void) | undefined;
  private onDeselectBuilding: (() => void) | undefined;
  private onHoverBuilding:
    ((buildingId: string | undefined) => void) | undefined;
  private onSiblingBuildingOverlap: (() => void) | undefined;

  /**
   * Draft shape state for drawing new buildings.
   *
   * NOTE: Draft shape is stored in {@link MapStateObservables.draftShape$} so the map has a
   * single source of truth for all geometry state.
   */
  private destroy$ = new Subject<void>();
  /** Serializes draft clicks so an in-flight await cannot overlap the next click. */
  private draftClickQueue: Promise<void> = Promise.resolve();
  /** Latest local edit; older failed saves must not roll it back. */
  private persistRevision = 0;
  /**
   * Bumped in {@link destroy} so a persist that finishes after re-attach
   * cannot revert storeys onto the new map instance.
   */
  private persistEpoch = 0;
  /** True after {@link destroy} until the same instance is {@link attach}ed again. */
  private isDestroyed = false;
  /**
   * Bumped on Esc, leaving pen, or destroy so clicks already in
   * {@link draftClickQueue} are dropped instead of writing after persist.
   */
  private draftClickEpoch = 0;
  private draftInteractionSub?: Subscription;
  // Henrik: point drags are applied via handlers (no external subscription needed).
  private locationPinModeSub?: Subscription;
  private zoomAutoSwitchSub?: Subscription;
  private isRegisteredInstance = false;
  private vertexInteractionSub?: Subscription;
  private pendingPointDeletion: PendingPointDeletion = undefined;
  private isReadonly = false;
  private locationDragCommitPending = false;
  /** Latest pin target while its project update is still reaching map state. */
  private pendingPinLocation: Coordinate | undefined;
  private pinLocationCommitSequence = 0;
  private draftPropagationStoreyIds: Storey['id'][] | undefined;

  // Snapshot of storeys at the start of an active point drag.
  private pointMoveBaseStoreys: Storey[] | undefined;

  get map(): Map | undefined {
    return this.mapInstance;
  }

  get isLoaded(): boolean {
    return this.loaded;
  }

  get isExactFacadeMode(): boolean {
    return this.facadeModeActive && this.facadeTransitionProgress === 1;
  }

  /** Project a DOM overlay anchor with the active exact-facade projection. */
  projectFacadeCoordinate(
    coordinate: Coordinate,
    elevationMeters: number,
  ): Point2D | undefined {
    return this.threeJs?.projectFacadeCoordinate(coordinate, elevationMeters);
  }

  /**
   * Capture a screenshot of the current map canvas while temporarily hiding
   * interactive overlays (points, lines, extrusions).
   */
  async captureScreenshot(options: MapScreenshotOptions = {}): Promise<string> {
    const map = this.mapInstance;
    if (!map) throw new Error('Map is not initialized');
    const { hiddenLayers = DEFAULT_SCREENSHOT_HIDDEN_LAYERS } = options;
    const toggledLayers: Array<{
      id: MapLayerIds;
      visibility: 'visible' | 'none' | mapboxgl.Expression | undefined;
    }> = [];
    const paintOverrides: Array<{
      id: MapLayerIds;
      property: MapPaintPropertyName;
      value: MapPaintValue;
    }> = [];
    const filterOverrides: Array<{
      id: MapLayerIds;
      filter: mapboxgl.Expression | undefined;
    }> = [];

    for (const layerId of hiddenLayers) {
      if (!map.getLayer(layerId)) continue;
      const currentVisibility = map.getLayoutProperty(layerId, 'visibility') as
        'visible' | 'none' | mapboxgl.Expression | undefined;
      toggledLayers.push({ id: layerId, visibility: currentVisibility });
      map.setLayoutProperty(layerId, 'visibility', 'none');
    }

    for (const override of SCREENSHOT_PAINT_OVERRIDES) {
      if (!map.getLayer(override.id)) continue;
      const entries = Object.entries(override.properties) as Array<
        [MapPaintPropertyName, MapPaintValue | undefined]
      >;
      for (const [property, value] of entries) {
        if (typeof value === 'undefined') continue;
        const previous = map.getPaintProperty(override.id, property);
        paintOverrides.push({ id: override.id, property, value: previous });
        map.setPaintProperty(override.id, property, value);
      }
    }

    for (const override of SCREENSHOT_FILTER_OVERRIDES) {
      if (!map.getLayer(override.id)) continue;
      filterOverrides.push({
        id: override.id,
        filter: map.getFilter(override.id) as mapboxgl.Expression | undefined,
      });
      map.setFilter(override.id, override.filter);
    }

    try {
      await sleep(200);
      await waitForNextRender(map);
      const canvas = map.getCanvas();
      return canvas.toDataURL('image/png');
    } finally {
      for (const { id, visibility } of toggledLayers) {
        map.setLayoutProperty(
          id,
          'visibility',
          (visibility ?? 'visible') as any,
        );
      }
      for (const { id, property, value } of paintOverrides) {
        map.setPaintProperty(id, property, value);
      }
      for (const { id, filter } of filterOverrides) {
        if (typeof filter === 'undefined') {
          map.setFilter(id, undefined);
        } else {
          map.setFilter(id, filter);
        }
      }
      await waitForNextRender(map);
    }
  }

  /**
   * Snapshot the painted map for PDF export.
   * Unlike {@link captureScreenshot}, this keeps building extrusions and current
   * paint so the report cover matches the on-screen 3D view.
   */
  async captureVisibleFrame(): Promise<string> {
    const map = this.mapInstance;
    if (!map) throw new Error('Map is not initialized');
    await waitForNextRender(map);
    const dataUrl = snapshotCanvasOntoBackground(
      map.getCanvas(),
      COVER_MAP_SNAPSHOT_BACKGROUND,
    );
    if (!dataUrl) throw new Error('Cover map snapshot could not be read');
    return dataUrl;
  }

  /**
   * Create a new design map controller.
   * @param accessToken Mapbox access token
   * @param initialLocation Initial {@link Coordinate} used for centering
   */
  constructor(accessToken: string, initialLocation: Coordinate) {
    mapboxgl.accessToken = accessToken;
    this.state = {
      storeys$: new BehaviorSubject<Storey[]>([]),
      originalStoreys$: new BehaviorSubject<Storey[]>([]),
      draftShape$: new BehaviorSubject<DraftShape | undefined>(undefined),
      selectedStoreyId$: new BehaviorSubject<Storey['id'] | undefined>(
        undefined,
      ),
      location$: new BehaviorSubject<Coordinate>(initialLocation),
      locationPinMode$: new BehaviorSubject<boolean>(false),
      penMode$: new BehaviorSubject<boolean>(false),
      otherBuildings$: new BehaviorSubject<Building[]>([]),
      hasBuildingSelection$: new BehaviorSubject<boolean>(true),
      isBuildingOutlineSelected$: new BehaviorSubject<boolean>(false),
      hoveredBuildingId$: new BehaviorSubject<string | undefined>(undefined),
      hoveredStoreyId$: new BehaviorSubject<string | undefined>(undefined),
      storeyHeightFocus$: new Subject<string>(),
    };
    this.snapHandler = new SnapAndBoundaries(this.state);
  }

  /**
   * Lazily create and cache the {@link MapEventHandler}.
   * @returns {@link MapEventHandler} or undefined when map is missing
   */
  get events(): MapEventHandler | undefined {
    if (!this.mapInstance) return undefined;
    if (!this.eventHandler) {
      this.eventHandler = new MapEventHandler(
        this.mapInstance,
        this.state,
        this.snapHandler,
      );
      this.eventHandler.setBlueprintUnderlayProvider(() =>
        this.blueprintUnderlay.getUnderlay(),
      );
      this.eventHandler.setDesignMapTool(this.designMapTool);
      this.eventHandler.setBuildingFootprintTemplate(
        this.buildingFootprintTemplate,
        this.customBuildingFootprintDrawingActive,
      );
      this.eventHandler.setCurrentBuildingId(this.storeysBuildingId);
      this.eventHandler.setBlueprintSelectionHandlers({
        hitTest: (event) => this.hitTestBlueprint(event),
        hitTestClientPoint: (clientX, clientY) =>
          this.hitTestBlueprintClientPoint(clientX, clientY),
        onSelectRequest: () => this.onBlueprintSelectRequest?.(),
        onDeselectRequest: () => this.onBlueprintDeselectRequest?.(),
        isSelected: () => this.blueprintUnderlay.getMode() !== 'locked',
        onContextMenuRequest: (position) =>
          this.onBlueprintContextMenuRequest?.(position),
      });
      if (this.onBuildingTranslate) {
        this.eventHandler.setBuildingTranslationHandler(
          this.pinTranslationHandler(this.onBuildingTranslate),
        );
      }
      if (this.onSelectBuilding) {
        this.eventHandler.setBuildingSelectionHandler(this.onSelectBuilding);
      }
      if (this.onDeselectBuilding) {
        this.eventHandler.setBuildingDeselectionHandler(
          this.onDeselectBuilding,
        );
      }
      if (this.onHoverBuilding) {
        this.eventHandler.setBuildingHoverHandler(this.onHoverBuilding);
      }
    }
    return this.eventHandler;
  }

  /**
   * Expose live interaction observables for UI consumers.
   * @returns Collection of observables or undefined when map is missing
   */
  getInteractionStreams():
    | {
        hoverState$: BehaviorSubject<MapHoverState | undefined>;
        pointMove$: Observable<PointPayload | undefined>;
        draftPreview$: Observable<DraftPreviewPayload | undefined>;
        tooltip$: Observable<MapTooltipPayload[]>;
      }
    | undefined {
    const eventHandler = this.events;
    if (!eventHandler) return undefined;
    return {
      hoverState$: eventHandler.getHoverState$(),
      pointMove$: eventHandler.getPointMove$(),
      draftPreview$: eventHandler.getDraftPreview$(),
      tooltip$: eventHandler.getTooltip$(),
    };
  }

  /** Keep geometry labels stable while their numeric input owns focus. */
  setGeometryMeasurementFocus(
    focus: MapGeometryMeasurementFocus | undefined,
  ): void {
    this.events?.setGeometryMeasurementFocus(focus);
  }

  getSingleSelectedCorner():
    { shapeId: Shape['id']; pointIndex: number } | undefined {
    const selections = this.eventHandler?.getSelections();
    const pointIndexes = selections?.pointIndexes;
    const pointIndex = pointIndexes?.length === 1 ? pointIndexes[0] : undefined;
    return selections?.shapeId !== undefined && pointIndex !== undefined
      ? { shapeId: selections.shapeId, pointIndex }
      : undefined;
  }

  /** Return the longest editable segment on the explicitly selected storey. */
  getLargestSelectedStoreySegment(): StoreySegmentReference | undefined {
    const storey = getStoreyById(
      this.state.storeys$.getValue(),
      this.state.selectedStoreyId$.getValue(),
    );
    return getLargestStoreySegment(storey);
  }

  /**
   * Edit a facade floor height using the same stack/mezzanine rules as storey settings.
   */
  async resizeStoreyHeight(
    storeyId: string,
    heightMeters: number,
    onLocalApplied?: () => void,
  ): Promise<boolean> {
    if (
      this.isReadonly ||
      this.isDestroyed ||
      !Number.isFinite(heightMeters) ||
      heightMeters < STOREY_MIN_HEIGHT
    )
      return false;
    const storeys = this.state.storeys$.getValue();
    const storey = storeys.find(({ id }) => id === storeyId);
    if (!storey) return false;
    if (storey.height === heightMeters) {
      onLocalApplied?.();
      return true;
    }
    const updated = mutateStoreys.update(storeys, {
      id: storeyId,
      height: heightMeters,
    });
    if (updated.find(({ id }) => id === storeyId)?.height !== heightMeters)
      return false;
    this.state.storeys$.next(updated);
    onLocalApplied?.();
    await this.commit({ reason: 'storey-height' });
    return true;
  }

  /** Set an edge's length by moving its endpoints, then persist the geometry. */
  async resizeSegmentLength(
    segment: MapTooltipSegmentRef,
    lengthMeters: number,
    onLocalApplied?: () => void,
  ): Promise<boolean> {
    if (this.isReadonly || this.isDestroyed) return false;

    const baseStoreys = this.state.storeys$.getValue();
    const storeyId = this.state.selectedStoreyId$.getValue();
    const storey = getStoreyById(baseStoreys, storeyId);
    const shape = storey?.shapes.find(
      (candidate) => candidate.id === segment.shapeId,
    );
    if (!storey || !shape) return false;

    const plan = planCommittedSegmentResize({
      points: shape.points,
      startIndex: segment.startIndex,
      endIndex: segment.endIndex,
      lengthMeters,
      manualSegmentTimestamps: getManualSegmentLengthTimestamps({
        storeyId: storey.id,
        shapeId: shape.id,
        pointCount: shape.points.length,
      }),
    });
    if (!plan) return false;

    const pointMoves = buildPointMoves({
      baseStoreys,
      storeyId: storey.id,
      shapeId: shape.id,
      pointIndex: plan.startIndex,
      offset: [0, 0],
      selectedPointIndexes: [...plan.nextPointPositions.keys()],
      targetPositions: plan.nextPointPositions,
    });
    if (!pointMoves.length) return false;

    let updatedStoreys = mutateStoreys.many.points.move(
      baseStoreys,
      ...pointMoves,
    );
    const updatedStorey = getStoreyById(updatedStoreys, storey.id);
    if (!updatedStorey) return false;

    if (designGuards.is.mezzanineStorey(updatedStorey)) {
      const parentBelow = getStoreyBelow(updatedStoreys, updatedStorey);
      if (parentBelow) {
        updatedStoreys = clampMezzanineStoreysInStorey(
          updatedStoreys,
          parentBelow,
        );
      }
    } else {
      updatedStoreys = clampMezzanineStoreysInStorey(
        updatedStoreys,
        updatedStorey,
      );
    }

    const finalShape = getStoreyById(updatedStoreys, storey.id)?.shapes.find(
      (candidate) => candidate.id === shape.id,
    );
    const finalStart = finalShape?.points[segment.startIndex];
    const finalEnd = finalShape?.points[segment.endIndex];
    if (!finalShape || !finalStart || !finalEnd) return false;
    const finalLength = Math.hypot(
      finalEnd[0] - finalStart[0],
      finalEnd[1] - finalStart[1],
    );
    const retainedPlannedPositions = [...plan.nextPointPositions].every(
      ([index, expected]) => {
        const actual = finalShape.points[index];
        return (
          actual &&
          Math.hypot(actual[0] - expected[0], actual[1] - expected[1]) <= 1e-6
        );
      },
    );

    // Numeric edits promise an exact length. Reject instead of silently
    // accepting geometry altered by polygon, mezzanine, or cutout constraints.
    if (
      Math.abs(finalLength - lengthMeters) > 1e-6 ||
      !retainedPlannedPositions ||
      isPolygonSelfIntersecting(finalShape.points) ||
      this.wouldInvalidateCutouts(storey.id, shape.id, updatedStoreys) ||
      this.hasInvalidChangedCutoutMargin(baseStoreys, updatedStoreys)
    ) {
      return false;
    }

    this.state.storeys$.next(updatedStoreys);
    onLocalApplied?.();
    const saved = await this.commit();
    // A cancelled snapshot resolves. The mark is only for a length that stuck.
    if (!saved) return true;
    recordManualSegmentLengthEdit({
      storeyId: storey.id,
      shapeId: shape.id,
      pointCount: finalShape.points.length,
      startIndex: segment.startIndex,
      endIndex: segment.endIndex,
    });
    return true;
  }

  /** Set one corner angle by moving only its vertex; both neighbors stay fixed. */
  async resizeCornerAngle(
    corner: MapTooltipCornerAngleRef,
    angleDegrees: number,
    onLocalApplied?: () => void,
  ): Promise<boolean> {
    if (this.isReadonly || this.isDestroyed) return false;
    const baseStoreys = this.state.storeys$.getValue();
    const storeyId = this.state.selectedStoreyId$.getValue();
    const storey = getStoreyById(baseStoreys, storeyId);
    const shape = storey?.shapes.find(
      (candidate) => candidate.id === corner.shapeId,
    );
    if (!storey || !shape) return false;

    const plan = planCornerAngleEdit({
      points: shape.points,
      pointIndex: corner.pointIndex,
      angleDegrees,
      manualAngleTimestamps: getManualCornerAngleTimestamps({
        storeyId: storey.id,
        shapeId: shape.id,
        pointCount: shape.points.length,
      }),
    });
    if (!plan) return false;

    const targetPositions = new globalThis.Map<number, Point2D>([
      [plan.pointIndex, plan.nextPoint],
    ]);
    const pointMoves = buildPointMoves({
      baseStoreys,
      storeyId: storey.id,
      shapeId: shape.id,
      pointIndex: plan.pointIndex,
      offset: [0, 0],
      selectedPointIndexes: [plan.pointIndex],
      targetPositions,
    });
    if (!pointMoves.length) return false;

    let updatedStoreys = mutateStoreys.many.points.move(
      baseStoreys,
      ...pointMoves,
    );
    const updatedStorey = getStoreyById(updatedStoreys, storey.id);
    if (!updatedStorey) return false;
    if (designGuards.is.mezzanineStorey(updatedStorey)) {
      const parentBelow = getStoreyBelow(updatedStoreys, updatedStorey);
      if (parentBelow) {
        updatedStoreys = clampMezzanineStoreysInStorey(
          updatedStoreys,
          parentBelow,
        );
      }
    } else {
      updatedStoreys = clampMezzanineStoreysInStorey(
        updatedStoreys,
        updatedStorey,
      );
    }

    const finalShape = getStoreyById(updatedStoreys, storey.id)?.shapes.find(
      (candidate) => candidate.id === shape.id,
    );
    const finalAngle = finalShape
      ? getPolygonCornerAngleDegrees(finalShape.points, corner.pointIndex)
      : undefined;
    const finalPoint = finalShape?.points[corner.pointIndex];
    if (
      !finalShape ||
      !finalPoint ||
      finalAngle === undefined ||
      Math.abs(finalAngle - angleDegrees) > 1e-4 ||
      Math.hypot(
        finalPoint[0] - plan.nextPoint[0],
        finalPoint[1] - plan.nextPoint[1],
      ) > 1e-6 ||
      isPolygonSelfIntersecting(finalShape.points) ||
      this.wouldInvalidateCutouts(storey.id, shape.id, updatedStoreys) ||
      this.hasInvalidChangedCutoutMargin(baseStoreys, updatedStoreys)
    ) {
      return false;
    }

    this.state.storeys$.next(updatedStoreys);
    onLocalApplied?.();
    const saved = await this.commit();
    if (!saved) return true;
    recordManualCornerAngleEdit({
      storeyId: storey.id,
      shapeId: shape.id,
      pointCount: finalShape.points.length,
      pointIndex: corner.pointIndex,
    });
    return true;
  }

  /** Set the requested, or every feasible, 45°–135° corner to 90°. */
  async squareEligibleCornerAngles(
    shapeId: Shape['id'],
    requestedPointIndexes?: readonly number[],
  ): Promise<boolean> {
    if (this.isReadonly || this.isDestroyed) return false;
    const baseStoreys = this.state.storeys$.getValue();
    const storeyId = this.state.selectedStoreyId$.getValue();
    const storey = getStoreyById(baseStoreys, storeyId);
    const shape = storey?.shapes.find((candidate) => candidate.id === shapeId);
    if (!storey || !shape) return false;
    const plan = planEligibleCornerRightAngles(
      shape.points,
      requestedPointIndexes,
    );
    if (!plan) return false;

    const targetPositions = new globalThis.Map<number, Point2D>(
      plan.nextPoints.map((point, index) => [index, point]),
    );
    const pointMoves = buildPointMoves({
      baseStoreys,
      storeyId: storey.id,
      shapeId: shape.id,
      pointIndex: plan.pointIndexes[0] ?? 0,
      offset: [0, 0],
      selectedPointIndexes: plan.nextPoints.map((_, index) => index),
      targetPositions,
    });
    if (!pointMoves.length) return false;

    let updatedStoreys = mutateStoreys.many.points.move(
      baseStoreys,
      ...pointMoves,
    );
    const updatedStorey = getStoreyById(updatedStoreys, storey.id);
    if (!updatedStorey) return false;
    if (designGuards.is.mezzanineStorey(updatedStorey)) {
      const parentBelow = getStoreyBelow(updatedStoreys, updatedStorey);
      if (parentBelow) {
        updatedStoreys = clampMezzanineStoreysInStorey(
          updatedStoreys,
          parentBelow,
        );
      }
    } else {
      updatedStoreys = clampMezzanineStoreysInStorey(
        updatedStoreys,
        updatedStorey,
      );
    }

    const finalShape = getStoreyById(updatedStoreys, storey.id)?.shapes.find(
      (candidate) => candidate.id === shape.id,
    );
    const retainedPlan = finalShape?.points.every((point, index) => {
      const expected = plan.nextPoints[index];
      return (
        expected !== undefined &&
        Math.hypot(point[0] - expected[0], point[1] - expected[1]) <= 1e-6
      );
    });
    const retainedAngles = plan.pointIndexes.every((pointIndex) => {
      const angle = finalShape
        ? getPolygonCornerAngleDegrees(finalShape.points, pointIndex)
        : undefined;
      return angle !== undefined && Math.abs(angle - 90) <= 0.05;
    });
    if (
      !finalShape ||
      !retainedPlan ||
      !retainedAngles ||
      isPolygonSelfIntersecting(finalShape.points) ||
      this.wouldInvalidateCutouts(storey.id, shape.id, updatedStoreys) ||
      this.hasInvalidChangedCutoutMargin(baseStoreys, updatedStoreys)
    ) {
      return false;
    }

    this.state.storeys$.next(updatedStoreys);
    const saved = await this.commit();
    if (!saved) return false;
    for (const pointIndex of plan.pointIndexes) {
      recordManualCornerAngleEdit({
        storeyId: storey.id,
        shapeId: shape.id,
        pointCount: finalShape.points.length,
        pointIndex,
      });
    }
    return true;
  }

  /**
   * Check whether point insertion is currently enabled.
   * @returns true when insert callbacks are registered
   */
  hasPointInsertCapability(): boolean {
    return true;
  }
  /**
   * Process draft shape interactions emitted by the event handler.
   * First click with Alt: inside an existing solid starts a cutout draft; outside all solids starts a new
   * footprint. Further points follow the draft’s cutout flag so Alt does not re-route a solid chain into
   * cutout validation (DEV-3020).
   * @param payload {@link DraftClickPayload} describing the action
   */
  private async handleDraftShapeInteraction(
    payload: DraftClickPayload,
  ): Promise<void> {
    // Transform/calibrate modes own pointer input; do not start wall drafts.
    if (this.blueprintUnderlay.isCapturingPointer()) return;

    if (payload.type === 'complete') {
      const draftShape = this.state.draftShape$.getValue();
      if (!draftShape) return;
      // Overlap rejects close only; keep vertices so the user can adjust.
      if (!this.applyDraftShapeComplete(draftShape)) {
        this.onSiblingBuildingOverlap?.();
        return;
      }
      this.state.draftShape$.next(undefined);
      return;
    }

    const storeys = this.state.storeys$.getValue();
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    const storey = getStoreyById(storeys, selectedStoreyId);
    const point2D = payload.position;
    const currentDraft = this.state.draftShape$.getValue();

    const payloadCutoutIntent =
      payload.type === 'add' && payload.cutoutMode === true;

    // First point only: Alt/Draw cutout intent, with solid fallbacks (DEV-3020 / DEV-3843).
    const isCutoutModeForNewDraft = currentDraft
      ? false
      : resolveNewDraftCutoutMode({
          cutoutIntent: payloadCutoutIntent,
          storey,
          point: point2D,
        });

    // Only when starting a draft: block a second solid footprint on the bottom storey if one already exists.
    // Courtyard exception: a solid inside an existing cutout is allowed (courtyard house — DEV-3843).
    if (storey && !currentDraft) {
      const storeyIndex = getStoreyIndex(storeys, storey);
      if (
        storeyIndex === 0 &&
        storey.shapes.length > 0 &&
        !isCutoutModeForNewDraft &&
        !isPointInsideStoreyCutout(point2D, storey)
      ) {
        return;
      }
    }

    this.eventHandler?.deselectShapesAndPoints();

    const draftIsCutout = currentDraft
      ? currentDraft.cutout === true
      : isCutoutModeForNewDraft;
    if (draftIsCutout) {
      if (storey && designGuards.is.mezzanineStorey(storey)) return;
      if (!getSolidShapes(storey).length) return;
      if (!isPointInsideStoreyShapes(point2D, storey)) return;
    } else if (
      isPointInsideStoreySolidOccupancy(point2D, storey) ||
      isPointInsideBuildingSolids(
        point2D,
        this.state.otherBuildings$.getValue(),
        storey ? getStoreyElevationRange(storeys, storey) : undefined,
      )
    ) {
      return;
    }

    let nextPoint = point2D;

    if (
      !currentDraft &&
      payload.type === 'add' &&
      payload.rebasedBuildingLocation
    ) {
      // Keep blueprint fixed in the world when the first click rebases location.
      if (payload.blueprintUnderlayOffset) {
        const underlay = this.blueprintUnderlay.getUnderlay();
        if (underlay) {
          this.blueprintUnderlay.updateUnderlayAndNotify(
            translateBlueprintUnderlay(
              underlay,
              payload.blueprintUnderlayOffset,
            ),
          );
        }
      }
      if (payload.coordinate) {
        const persisted = await this.persistRebasedLocation({
          coordinate: payload.coordinate,
          previousLocation: payload.previousLocation,
          epoch: this.draftClickEpoch,
        });
        if (!persisted) return;
      }
      nextPoint = [0, 0];
    }

    if (
      !currentDraft &&
      payload.type === 'add' &&
      this.buildingFootprintTemplate
    ) {
      const templateDrafts = createBuildingFootprintTemplateDrafts(
        this.buildingFootprintTemplate,
        nextPoint,
        this.mapInstance?.getBearing() ?? 0,
        0,
      );
      if (!this.applyDraftShapesComplete(templateDrafts)) {
        this.onSiblingBuildingOverlap?.();
      }
      return;
    }

    const nextDraft = currentDraft
      ? mutateShape.point.add(currentDraft, nextPoint)
      : createDraftShape({
          points: [nextPoint],
          cutout: draftIsCutout,
        });
    this.state.draftShape$.next(nextDraft);
  }

  /**
   * Persist a completed draft onto the selected storey.
   * @returns false when the draft is kept because it overlaps a sibling building
   */
  private applyDraftShapeComplete(draftShape: DraftShape): boolean {
    const storeys = this.state.storeys$.getValue();
    const result = applyCompletedDraftToStoreys(storeys, draftShape, {
      selectedStoreyId:
        this.state.selectedStoreyId$.getValue() ?? storeys[0]?.id,
      otherBuildings: this.state.otherBuildings$.getValue(),
      propagateToStoreyIds: this.draftPropagationStoreyIds,
    });
    switch (result.status) {
      case 'overlap':
        // Reject close only; caller keeps the in-progress draft.
        return false;
      case 'rejected':
        return true;
      case 'applied':
        this.updateStoreys(() => result.storeys);
        this.draftPropagationStoreyIds = undefined;
        this.commit();
        this.onDraftComplete?.();
        return true;
      default:
        return assertUnreachable(result);
    }
  }

  /** Persist a preset's solid and courtyard cutouts as one atomic edit. */
  private applyDraftShapesComplete(
    draftShapes: DraftShape[],
    preserveViewport = false,
  ): boolean {
    const currentStoreys = this.state.storeys$.getValue();
    const selectedStoreyId =
      this.state.selectedStoreyId$.getValue() ?? currentStoreys[0]?.id;
    const result = applyCompletedDraftsToStoreys(currentStoreys, draftShapes, {
      selectedStoreyId,
      otherBuildings: this.state.otherBuildings$.getValue(),
      propagateToStoreyIds: this.draftPropagationStoreyIds,
    });
    if (result.status !== 'applied') return false;

    this.preserveTemplatePlacementViewport = preserveViewport;
    this.updateStoreys(() => result.storeys);
    this.draftPropagationStoreyIds = undefined;
    void this.commit().finally(() => {
      this.preserveTemplatePlacementViewport = false;
    });
    this.onDraftComplete?.();
    return true;
  }

  /**
   * Restore map `location$` after a failed or skipped empty-site rebase persist.
   */
  private rollbackRebasedLocation(previousLocation?: Coordinate): void {
    if (previousLocation) this.state.location$.next(previousLocation);
  }

  /**
   * Persist a location rebase from the first empty-site draft click.
   * Rolls {@link MapStateObservables.location$} back when persist fails, the
   * callback is missing, or the draft was discarded (epoch mismatch) so the
   * vertex is not written against an unsaved or abandoned origin.
   * @returns False when the draft vertex must not be added
   */
  private async persistRebasedLocation(input: {
    coordinate: Coordinate;
    previousLocation?: Coordinate;
    epoch: number;
  }): Promise<boolean> {
    const shouldApply = () =>
      shouldApplyDraftAfterAwait({
        isDestroyed: this.isDestroyed,
        startedEpoch: input.epoch,
        currentEpoch: this.draftClickEpoch,
      });
    if (!this.onBuildingTranslate || !shouldApply()) {
      this.rollbackRebasedLocation(input.previousLocation);
      return false;
    }
    try {
      await this.onBuildingTranslate(input.coordinate, 'origin-rebase');
    } catch (err) {
      console.warn('Failed to update building location', err);
      this.rollbackRebasedLocation(input.previousLocation);
      return false;
    }
    return shouldApply();
  }

  /**
   * Toggle leftover pen-tool mode. Entering or leaving clears any in-progress
   * draft so drawing always starts fresh.
   * @param active Whether the pen tool should be active
   */
  setPenMode(active: boolean): void {
    if (this.state.penMode$.getValue() === active) return;
    this.eventHandler?.deselectShapesAndPoints();
    this.clearDraftShape();
    this.state.penMode$.next(active);
  }

  /**
   * Drop queued or in-flight draft clicks (Esc, leaving pen, destroy).
   * First-click location persist checks this epoch before writing a vertex.
   */
  private invalidateInFlightDraftClicks(): void {
    this.draftClickEpoch += 1;
  }

  /**
   * Provide non-selected buildings for static rendering (DEV-2576).
   * @param buildings Buildings that are not currently being edited
   */
  setOtherBuildings(buildings: Building[]): void {
    this.state.otherBuildings$.next(buildings);
  }

  /**
   * Register a callback when a draft close is rejected because the polygon
   * shares interior area with a sibling building (DEV-2576).
   */
  enableSiblingBuildingOverlapWarning({
    onOverlap,
  }: {
    onOverlap: () => void;
  }): void {
    if (this.isReadonly) return;
    this.onSiblingBuildingOverlap = onOverlap;
  }

  /**
   * Register a callback that selects another building from its footprint.
   * Pen mode keeps an in-progress draft so an empty-slot click can still close
   * into that building.
   * @param onSelect Called with the clicked building id
   * @param onOpen Called with a double-clicked building id
   * @param onDeselect Called when a background click clears the selection
   * @param onHover Called with the building id under the pointer
   */
  enableBuildingSelection({
    onSelect,
    onOpen,
    onDeselect,
    onHover,
  }: {
    onSelect: (buildingId: string) => void;
    onOpen?: (buildingId: string, storeyId?: string) => void;
    onDeselect?: () => void;
    onHover?: (buildingId: string | undefined) => void;
  }): void {
    if (this.isReadonly) return;
    this.onSelectBuilding = (buildingId) => {
      if (shouldClearDraftOnBuildingSelect(this.state.penMode$.getValue())) {
        this.clearDraftShape();
      }
      onSelect(buildingId);
    };
    this.eventHandler?.setBuildingSelectionHandler(this.onSelectBuilding);
    this.eventHandler?.setBuildingOpenHandler(onOpen);
    this.onDeselectBuilding = onDeselect;
    if (onDeselect) {
      this.eventHandler?.setBuildingDeselectionHandler(onDeselect);
    }
    this.onHoverBuilding = onHover;
    if (onHover) {
      this.eventHandler?.setBuildingHoverHandler(onHover);
    }
  }

  /**
   * Tell the map whether a building is currently selected (DEV-2576).
   * With no selection there is nothing to draw into, so drafting and the
   * click-anywhere affordances stay off.
   * @param hasSelection True while a building is selected
   */
  /**
   * Outline the selected building's whole volume in blue (DEV-2576).
   * @param isSelected True while the building itself is selected
   */
  setBuildingOutlineSelected(isSelected: boolean): void {
    if (this.state.isBuildingOutlineSelected$.getValue() === isSelected) return;
    this.state.isBuildingOutlineSelected$.next(isSelected);
    this.eventHandler?.refreshHoveredBuildingAtCurrentPointer();
  }

  /** Highlight a hovered static building without selecting it. */
  setHoveredBuilding(buildingId: string | undefined): void {
    if (this.state.hoveredBuildingId$.getValue() === buildingId) return;
    this.state.hoveredBuildingId$.next(buildingId);
  }

  setHasBuildingSelection(hasSelection: boolean): void {
    if (this.state.hasBuildingSelection$.getValue() === hasSelection) return;
    this.state.hasBuildingSelection$.next(hasSelection);
  }

  /**
   * Drop the in-progress draft on the interactive Design map, if any.
   * Also bumps {@link draftClickEpoch} so an in-flight first click cannot write
   * a vertex onto the building selected after this discard.
   */
  static discardActiveDraft(): void {
    DesignMap.activeInteractiveController?.clearDraftShape();
  }

  /**
   * Clear the draft shape and upper-storey propagation ids.
   * Always resets {@link draftPropagationStoreyIds} even when draft is already gone,
   * so a prior triangle→draft conversion cannot write onto the next building.
   * Bumps {@link draftClickEpoch} so queued first-click persists are dropped.
   */
  private clearDraftShape(): void {
    this.invalidateInFlightDraftClicks();
    this.draftPropagationStoreyIds = undefined;
    if (this.state.draftShape$.getValue()) {
      this.state.draftShape$.next(undefined);
    }
    const selections = this.eventHandler?.getSelectionObservables();
    selections?.draftShape$.next(false);
  }

  /**
   * Attach and initialize the map to a DOM container.
   * Safe to call multiple times; any existing instance will be destroyed first.
   */
  async attach(
    container: HTMLDivElement,
    options?: Omit<MapOptions, 'container'> & {
      verticalFieldOfView?: number;
      padding?: mapboxgl.PaddingOptions;
    },
  ): Promise<DesignMap> {
    if (!container) throw new Error('Container is required to attach the map');
    this.destroy();
    this.isDestroyed = false;
    // Recreate snapHandler after destroy (destroy sets it to undefined)
    this.snapHandler = new SnapAndBoundaries(this.state);
    // destroy() drops this subscription; rebind so pin zoom still syncs overlays.
    this.bindLocationPinModeEffects();
    try {
      const location = this.state.location$.getValue();
      const {
        verticalFieldOfView = DEFAULT_MAP_FOV,
        padding,
        ...mapOptions
      } = options ?? {};
      this.isDisplayOnlyInstance = options?.interactive === false;
      const map = new mapboxgl.Map({
        ...mapConfigDefaults,
        center: [location.lng, location.lat],
        ...mapOptions,
        projection: mapConfigDefaults.projection,
        container,
      });
      // Padding is camera state, not a Map constructor option.
      if (padding) map.jumpTo({ padding });
      this.mapInstance = map;
      this.appliedVerticalFov = undefined;
      this.syncVerticalFovForPitch();
      if (options?.verticalFieldOfView !== undefined)
        this.applyVerticalFov(verticalFieldOfView);
      // Standard fence layers evaluate line-progress without vector lineMetrics.
      // Bind before the first tile parse so Mapbox does not warnOnce.
      bindDisableImportedLineProgressEvaluation(map);
      this.registerInstance();
      const eventHandler = new MapEventHandler(
        map,
        this.state,
        this.snapHandler,
      );
      this.eventHandler = eventHandler;
      eventHandler.setHasPointInsertCapability(true);
      eventHandler.setInteractionResetHandler(() => {
        this.renderer?.refreshVertices();
      });
      eventHandler.setBlueprintUnderlayProvider(() =>
        this.blueprintUnderlay.getUnderlay(),
      );
      eventHandler.setBlueprintInkSnapProvider((point, maxDistanceMeters) =>
        this.blueprintUnderlay.snapToInk(point, maxDistanceMeters),
      );
      eventHandler.setDesignMapTool(this.designMapTool);
      eventHandler.setBlueprintSelectionHandlers({
        hitTest: (event) => this.hitTestBlueprint(event),
        hitTestClientPoint: (clientX, clientY) =>
          this.hitTestBlueprintClientPoint(clientX, clientY),
        onSelectRequest: () => this.onBlueprintSelectRequest?.(),
        onDeselectRequest: () => this.onBlueprintDeselectRequest?.(),
        isSelected: () => this.blueprintUnderlay.getMode() !== 'locked',
        onContextMenuRequest: (position) =>
          this.onBlueprintContextMenuRequest?.(position),
      });
      this.bindVertexInteractionHandlers(eventHandler);
      if (this.onBuildingTranslate) {
        eventHandler.setBuildingTranslationHandler(
          this.pinTranslationHandler(this.onBuildingTranslate),
        );
      }

      map.addControl(
        new mapboxgl.AttributionControl({ compact: true }),
        'bottom-right',
      );

      // Wait for style to load before creating renderer and loading icons
      await Promise.all([
        waitForStyleLoad(map),
        waitForMapLoad(map),
        loadMapIcons(map),
      ]);

      // TODO: Enable terrain when we have line and extrusion in level
      // enableTerrain(map);

      // Prevent canvas container from showing focus outline
      applyCanvasOutlineRemoval(map);

      this.renderer = new MapRenderer(map, this.state, {
        selections: eventHandler.getSelectionObservables(),
        hoverState$: eventHandler.getHoverState$(),
        pointMove$: eventHandler.getPointMove$(),
        draftCursorState$: eventHandler.getDraftCursorState$(),
        hasPointInsert: true,
        shapeTranslation$: eventHandler.getShapeTranslation$(),
        buildingRotationHover$: eventHandler.getBuildingRotationHover$(),
        locationPinCursor$: eventHandler.getLocationPinCursor$(),
      });
      this.renderer.setDesignMapTool(this.designMapTool);
      this.renderer.setBuildingFootprintTemplateActive(
        Boolean(this.buildingFootprintTemplate),
      );
      this.renderer.setShowNearbyBuildings(
        this.showBackgroundMap && this.showNearbyBuildings,
      );

      // Wireframe overlay layer that listens to map-event-handler streams.
      this.threeJs = new MapThreeJs(map, this.state, eventHandler, {
        mode: 'all-storeys',
      });
      this.threeJs.attach();
      eventHandler.setFacadePickRayResolver((screenPoint) =>
        this.threeJs?.getFacadePickRay(screenPoint),
      );
      eventHandler.setBuildingContourProjector((coordinate, elevation) =>
        this.threeJs?.projectFacadeCoordinate(coordinate, elevation),
      );

      // Enable draft-shape drawing by default; it only mutates local state and is persisted on save.
      this.bindDraftShapeEvents();

      this.blueprintUnderlay.attach(map);
      this.blueprintUnderlay.setLocation(this.state.location$.getValue());
      // Keep underlay elevation + selected-storey isolation in sync with the active floor.
      combineLatest([this.state.storeys$, this.state.selectedStoreyId$])
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.syncBlueprintUnderlayPresentation();
        });
      if (!this.isDisplayOnlyInstance || !this.showBackgroundMap) {
        this.syncLocationPinOverlays();
      }
      if (!this.backgroundOverlayListenerAttached) {
        this.backgroundOverlayListenerAttached = true;
        map.on('styledata', () => {
          if (this.isDisplayOnlyInstance && this.showBackgroundMap) return;
          this.syncLocationPinOverlays();
          if (this.measureIsolationActive) {
            this.hideMeasureIsolationLayers(map, {
              appearedDuringIsolation: true,
            });
          }
          this.blueprintUnderlay.handleStyleData();
        });
      }

      // Desktop navigation swaps Mapbox's mouse defaults while preserving touch.
      if (!this.isDisplayOnlyInstance) {
        // Shift is reserved for snap-off while drawing; Mapbox boxZoom would
        // otherwise steal Shift+click and block placing polygon vertices.
        map.boxZoom?.disable?.();
        this.middleMousePan.setCursorHandler((cursor) => {
          this.renderer?.setNavigationCursor(cursor);
        });
        this.middleMousePan.attach(map);
        this.mouseNavigation.setCursorHandler((cursor) => {
          if (this.middleMousePan.isActive()) return;
          this.renderer?.setNavigationCursor(cursor);
        });
        this.mouseNavigation.setBuildingRotationActiveProvider(() =>
          eventHandler.isBuildingRotationActive(),
        );
        this.mouseNavigation.setTemplatePreviewActiveProvider(() =>
          Boolean(this.buildingFootprintTemplate),
        );
        this.mouseNavigation.setPointDragActiveProvider(() =>
          eventHandler.isPointDragActive(),
        );
        this.mouseNavigation.setCtrlDrawingActiveProvider(
          () =>
            !this.buildingFootprintTemplate &&
            !navigator.userAgent.includes('Mac') &&
            eventHandler.isUnderlayDrawingActive(),
        );
        this.mouseNavigation.setBearingSnapDirectionsProvider(() => {
          return getBearingSnapDirectionsForSelection({
            hasBuildingSelection: this.state.hasBuildingSelection$.getValue(),
            selectedStoreys: this.state.storeys$.getValue(),
            otherBuildings: this.state.otherBuildings$.getValue(),
          });
        });
        this.mouseNavigation.setFacadeModeHandler((active) => {
          this.setFacadeModeActive(active);
        });
        this.mouseNavigation.setFacadeTransitionProgressHandler((progress) => {
          this.setFacadeTransitionProgressFromGesture(progress);
        });
        this.mouseNavigation.setFacadeTransitionEndHandler(() => {
          this.finishFacadeTransition();
        });
        this.mouseNavigation.setFacadeVerticalPanHandler((deltaYPixels) => {
          this.panFacadeViewVertically(deltaYPixels);
        });
        this.mouseNavigation.setFacadeVerticalPanEndHandler(() => {
          this.notifyCameraChange();
        });
        this.mouseNavigation.attach(map);
        this.attachSpacePanListeners();
        this.attachCameraChangeListener(map);
      }

      this.loaded = true;
      this.setupZoomAutoSwitch();
      this.syncReadonlyCameraToLocation();
      return this;
    } catch (err) {
      console.warn('Mapbox initialization failed', err);
      this.destroy();
      throw err;
    }
  }

  /**
   * Provide the full list of {@link Storey} objects to render.
   * Pass {@link options.buildingId} when the list belongs to a specific building
   * so a later {@link commit} persists to that building, not a newer selection.
   * @param storeys Storey collection
   * @param options.buildingId Building that owns {@link storeys}; omit to keep the last bound id
   */
  setStoreys(storeys: Storey[], options?: { buildingId?: string }): void {
    if (options) {
      this.storeysBuildingId = options.buildingId;
      this.eventHandler?.setCurrentBuildingId(this.storeysBuildingId);
    }
    if (storeys === this.latestReceivedStoreys) return;
    // Keep a baseline snapshot so "Escape" can revert draft edits deterministically.
    this.latestReceivedStoreys = storeys;
    this.state.storeys$.next(storeys);
    // Update original storeys for boundary enforcement in snapping
    this.state.originalStoreys$.next(storeys);
    // Keep selection stable when storeys are replaced from the server snapshot.
    this.selectStorey(this.state.selectedStoreyId$.getValue());
    this.eventHandler?.refreshHoveredBuildingAtCurrentPointer();
  }

  /**
   * Update the active map location and pan the map if needed.
   * @param location {@link Coordinate} to focus on
   */
  setLocation(location: Coordinate): void {
    const current = this.state.location$.getValue();
    const pendingPinLocation = this.pendingPinLocation;
    const matchesPendingPin =
      pendingPinLocation?.lat === location.lat &&
      pendingPinLocation.lng === location.lng;

    // Project/PVGIS responses may arrive out of order while the user moves the
    // pin repeatedly. Keep rendering the latest drag target until that exact
    // location reaches the project store.
    if (pendingPinLocation && !matchesPendingPin) return;
    if (matchesPendingPin) this.pendingPinLocation = undefined;

    // Don't move while user is dragging the location pin.
    if (this.events?.getLocationPinCursor$().getValue().dragging) return;

    // Consume only once this call applies the location. An ignored update is
    // not the camera movement the one-shot suppression was armed for.
    const suppressFlyTo = consumeDesignMapLocationFlyToSuppression();

    // If the location is the same, still ensure shared/read-only camera is aligned.
    if (current.lat === location.lat && current.lng === location.lng) {
      this.locationDragCommitPending = false;
      this.syncReadonlyCameraToLocation();
      this.blueprintUnderlay.setLocation(location);
      return;
    }

    this.state.location$.next(location);
    this.blueprintUnderlay.setLocation(location);
    const fromDrag = this.locationDragCommitPending;
    this.locationDragCommitPending = false;
    if (this.isReadonly) {
      this.syncReadonlyCameraToLocation();
    } else if (
      !fromDrag &&
      !suppressFlyTo &&
      !this.preserveTemplatePlacementViewport
    ) {
      this.mapInstance?.flyTo({
        center: [location.lng, location.lat],
        speed: 3.6,
      });
    }
  }

  /**
   * Location the map is currently showing.
   */
  getLocation(): Coordinate {
    return { ...this.state.location$.getValue() };
  }

  /**
   * Update the building location and fly the map camera to the new position.
   * Cancels an in-flight pin drag so that drag cannot block or overwrite this
   * place. A drag that is still moving the mouse can commit again on mouseup.
   * @param location {@link Coordinate} to fly to
   */
  flyToLocation(location: Coordinate): void {
    // Search replaces a pin save that has not finished. The exact-coordinate
    // hold would drop the saved search pin, and the drag result would move
    // the map back (DEV-3888).
    this.pendingPinLocation = undefined;
    this.pinLocationCommitSequence += 1;
    this.locationDragCommitPending = false;
    this.state.location$.next(location);
    this.blueprintUnderlay.setLocation(location);
    this.mapInstance?.flyTo({
      center: [location.lng, location.lat],
      speed: 3.6,
    });
  }

  /**
   * Fit the camera to a blueprint underlay so the whole sheet fills the Design
   * canvas — used right after placing a PDF import.
   */
  flyToBlueprintUnderlay(underlay: BlueprintUnderlay): void {
    const map = this.mapInstance;
    if (!map || !this.loaded) return;
    if (!(underlay.widthMeters > 0) || !(underlay.heightMeters > 0)) return;

    const location = this.state.location$.getValue();
    const corners = getBlueprintImageCoordinates(location, underlay);
    const bounds = new mapboxgl.LngLatBounds(corners[0], corners[0]);
    for (const corner of corners) {
      bounds.extend(corner);
    }

    // Cancel any in-flight pitch ease (e.g. import chrome → 2D) so it cannot
    // override this fit.
    map.stop();
    map.fitBounds(bounds, {
      padding: {
        top: 40,
        bottom: 40,
        left: 40 + getMapViewportLeftInset(map),
        right: 40,
      },
      duration: 900,
      essential: true,
      pitch: 0,
      bearing: 0,
      // Keep a usable tracing scale; tiny sheets should not zoom to street level.
      maxZoom: 20,
    });
  }

  /**
   * Center the camera on the site footprint at a medium zoom (comfortable
   * padding around the massing) — used by the out-of-view recenter bar.
   * Pass every building's storeys so Show frames the whole site.
   */
  flyToBuildingFocus(storeys: Storey[]): void {
    const map = this.mapInstance;
    if (!map || !this.loaded) return;

    const location = this.state.location$.getValue();
    const container = map.getContainer();
    const leftInset = getMapViewportLeftInset(map);
    const rightInset = map.getPadding?.().right ?? 0;
    const camera = getBuildingFocusCamera({
      location,
      storeys,
      containerWidth: container.clientWidth - leftInset - rightInset,
      containerHeight: container.clientHeight,
    });

    if (!camera) {
      map.flyTo({
        center: [location.lng, location.lat],
        zoom: 17,
        speed: 1.4,
        essential: true,
      });
      return;
    }

    map.flyTo({
      center: camera.center,
      zoom: camera.zoom,
      padding: {
        top: 0,
        bottom: 0,
        left: leftInset,
        right: rightInset,
      },
      speed: 1.4,
      essential: true,
    });
  }

  /**
   * Keep map camera center aligned with current location in shared/read-only mode.
   */
  private syncReadonlyCameraToLocation(): void {
    if (!this.isReadonly || !this.loaded) return;
    const map = this.mapInstance;
    if (!map) return;
    const location = this.state.location$.getValue();
    const center = map.getCenter();
    const centerChanged =
      center.lat !== location.lat || center.lng !== location.lng;
    if (!centerChanged) return;
    map.jumpTo({ center: [location.lng, location.lat] });
  }

  /**
   * Select a storey by id or storey object.
   * @param storeyOrId {@link Storey} or id to select, undefined clears selection
   */
  selectStorey(storeyOrId?: ItemOrItemId<Storey>): void {
    const id = storeyOrId ? getId(storeyOrId) : undefined;
    const storeys = this.state.storeys$.getValue();
    const selectedStoreyId = getStoreyById(storeys, id) ? id : undefined;
    const currentStoreyId = this.state.selectedStoreyId$.getValue();
    if (currentStoreyId !== selectedStoreyId) {
      // Clear shape/point selection when switching storeys.
      this.eventHandler?.deselectShapesAndPoints();
    }
    this.state.selectedStoreyId$.next(selectedStoreyId);
  }

  /**
   * Reset map state to the latest server snapshot.
   * Clears draft and point selections while retaining current storey selection.
   */
  reset(): void {
    this.invalidateInFlightDraftClicks();
    const map = this.mapInstance;
    if (map) {
      map.dragPan?.enable?.();
      this.renderer?.resetCursor();
    }
    this.eventHandler?.cancelInteractions();
    this.pendingPointDeletion = undefined;

    // Restore draft state back to the latest snapshot received from Map3d (not the current draft).
    this.state.storeys$.next(this.latestReceivedStoreys);
    this.state.draftShape$.next(undefined);
    this.draftPropagationStoreyIds = undefined;
    this.state.selectedStoreyId$.next(this.state.selectedStoreyId$.getValue());

    const selectionObservables = this.eventHandler?.getSelectionObservables();
    if (selectionObservables) {
      selectionObservables.pointIndexes$.next(undefined);
      selectionObservables.shapeId$.next(undefined);
      selectionObservables.draftShape$.next(false);
    }

    // Esc / abort also exits the pen tool, discarding any unclosed polygon.
    this.setPenMode(false);
  }

  /**
   * Enable building translation interactions.
   * @param onCommit Callback invoked with the committed {@link Coordinate}
   */
  enableBuildingTranslation({
    onCommit,
  }: {
    onCommit: BuildingTranslateHandler;
  }): void {
    if (this.isReadonly) return;
    const wrapped: BuildingTranslateHandler = async (location, reason) => {
      this.locationDragCommitPending = true;
      return await onCommit(location, reason);
    };
    this.onBuildingTranslate = wrapped;
    this.eventHandler?.setBuildingTranslationHandler(
      this.pinTranslationHandler(wrapped),
    );
  }

  /**
   * Commit one pin drag through {@link pinTranslationHandler}.
   * Tests use this so a recentered save can be checked without Mapbox.
   */
  commitPinTranslationForTests(
    location: Coordinate,
    onCommit: BuildingTranslateHandler,
  ): Promise<void> {
    return Promise.resolve(this.pinTranslationHandler(onCommit)(location));
  }

  /** The location pin only moves the location when the user drags it. */
  private pinTranslationHandler(
    handler: BuildingTranslateHandler,
  ): (location: Coordinate) => Promise<void> | void {
    return async (location) => {
      const sequence = ++this.pinLocationCommitSequence;
      this.pendingPinLocation = { ...location };
      try {
        const persisted = await handler(location, 'user');
        if (sequence !== this.pinLocationCommitSequence) return;
        // The saved pin is the footprint midpoint, which can differ from the
        // drag target. Release the exact-coordinate hold and show that pin.
        this.pendingPinLocation = undefined;
        if (!persisted) return;
        // Detach from the caller's object. It may be the project store coordinate.
        this.setLocation({ lat: persisted.lat, lng: persisted.lng });
      } catch (error) {
        if (sequence === this.pinLocationCommitSequence) {
          this.pendingPinLocation = undefined;
        }
        throw error;
      }
    };
  }

  /**
   * Register a callback for keyboard-driven storey navigation.
   * @param onNavigate Called with `'tab'` for Tab key or a 1-based index for Cmd/Ctrl+digit
   */
  enableStoreyNavigation({
    onNavigate,
  }: {
    onNavigate: (direction: 'tab' | number) => void;
  }): void {
    if (this.isReadonly) return;
    this.onStoreyNavigate = onNavigate;
  }

  /** Register a selection made by clicking a storey's visible map volume. */
  enableStoreySelection({
    onSelect,
  }: {
    onSelect: (storeyId: Storey['id']) => void;
  }): void {
    if (this.isReadonly) return;
    this.onStoreySelect = onSelect;
  }

  /**
   * Patch extrusion render parameters (blend mode, opacity, visibility) and trigger a repaint.
   * @param patch Partial {@link ExtrusionRenderParams} to merge into current params
   */
  setExtrusionParams(patch: Partial<ExtrusionRenderParams>): void {
    this.threeJs?.setExtrusionParams(patch);
  }

  /**
   * Toggle read-only mode for map interactions and rendering overlays.
   * Does not apply to the vertex lock used while a new building persist is in flight.
   * @param isReadonly When true, disable editing affordances
   */
  setReadonly(isReadonly: boolean): void {
    if (this.isReadonly === isReadonly) return;
    this.isReadonly = isReadonly;
    this.eventHandler?.setReadonly(isReadonly);
    this.threeJs?.setReadonly(isReadonly);
    if (isReadonly) {
      this.onCommit = undefined;
      this.onDuplicateBuilding = undefined;
      this.onBuildingTranslate = undefined;
      this.onStoreyNavigate = undefined;
      this.onStoreySelect = undefined;
      this.onSelectBuilding = undefined;
      this.onDeselectBuilding = undefined;
      this.onHoverBuilding = undefined;
      this.onSiblingBuildingOverlap = undefined;
      this.eventHandler?.setBuildingTranslationHandler(undefined);
      this.eventHandler?.setBuildingSelectionHandler(undefined);
      this.eventHandler?.setBuildingOpenHandler(undefined);
      this.eventHandler?.setBuildingDeselectionHandler(undefined);
      this.eventHandler?.setBuildingHoverHandler(undefined);
      this.mapInstance?.dragPan?.enable?.();
      this.setPenMode(false);
      this.syncReadonlyCameraToLocation();
    }
  }

  /**
   * Register a single commit callback for persisting the current map state.
   * Consumers should update {@link MapStateObservables.storeys$} locally and call {@link commit}.
   *
   * Henrik: This deliberately avoids multiple "commit" entry points; we persist whatever is currently
   * in the map state, similar to how Boverket expects a single coherent snapshot.
   */
  /** Persist the copy produced by an Alt-drag of a whole building. */
  enableBuildingDuplication(
    handler: (buildingId: string | undefined, delta: Point2D) => Promise<void>,
  ): void {
    this.onDuplicateBuilding = handler;
  }

  enableCommit({ onCommit }: { onCommit: StoreysCommitHandler }): void {
    if (this.isReadonly) return;
    this.onCommit = onCommit;
  }

  /**
   * Ensure mezzanine shapes are clamped inside their parent storey boundaries
   * before committing. Called after drag operations where real-time boundary
   * enforcement was relaxed (e.g. multi-select on mezzanine storeys).
   */
  private clampMezzanineStoreysBeforeCommit(): void {
    const storeys = this.state.storeys$.getValue();
    let clamped = storeys;
    for (const storey of storeys) {
      if (designGuards.is.mezzanineStorey(storey)) continue;
      clamped = clampMezzanineStoreysInStorey(clamped, storey);
    }
    // Insert boundary points on mezzanine edges that exit the parent polygon.
    clamped = insertMezzanineBoundaryPoints(clamped);
    if (clamped !== storeys) {
      this.state.storeys$.next(clamped);
    }
  }

  /**
   * Remove near-duplicate adjacent points from all shapes before commit.
   * Points within 0.1m of each other are merged to prevent degenerate geometry.
   */
  private mergeNearbyPointsBeforeCommit(): void {
    const storeys = this.state.storeys$.getValue();
    const merged = mergeNearbyPointsInStoreys(storeys);
    if (merged !== storeys) {
      this.state.storeys$.next(merged);
    }
  }

  /**
   * Persist the current storeys snapshot from {@link MapStateObservables.storeys$}.
   * The design persistence layer serializes optimistic writes and handles
   * overlapping saves. The storeys, building id, and handler
   * are captured now so a later selection or {@link destroy} cannot retarget or
   * drop this save.
   * {@link options.storeys} skips preprocess and local overwrite — used when
   * the pin was already rebased for display but persist must send the
   * pre-rebase geometry (same snapshot persist will normalize).
   * @returns false when the commit was skipped or cancelled because a newer
   * edit already changed the same fields. Real save failures reject.
   */
  async commit(options?: {
    storeys?: Storey[];
    reason?: StoreysCommitReason;
  }): Promise<boolean> {
    if (this.isReadonly || this.isDestroyed) return false;
    const handler = this.onCommit;
    if (!handler) return false;
    if (!options?.storeys) {
      this.clampMezzanineStoreysBeforeCommit();
      this.mergeNearbyPointsBeforeCommit();
    }
    const job: PersistStoreysJob = {
      handler,
      originalStoreys: this.state.originalStoreys$.getValue(),
      storeys: options?.storeys ?? this.state.storeys$.getValue(),
      buildingId: this.storeysBuildingId,
      reason: options?.reason,
      epoch: this.persistEpoch,
      revision: ++this.persistRevision,
    };
    if (!options?.storeys) {
      this.setStoreys(job.storeys);
    }
    return this.persistStoreys(job);
  }

  /**
   * Write a {@link PersistStoreysJob} captured at {@link commit}.
   * Jobs from a destroyed/reattached map (stale {@link persistEpoch}) are skipped.
   * Failures revert only the latest local edit, then rethrow to the caller.
   * A superseded snapshot is a cancellation: the handler resyncs the map, and
   * this method does not warn, revert, or rethrow. It resolves false so
   * callers can skip work that belongs only to a save that landed.
   * @returns false when the job was skipped or cancelled
   */
  private async persistStoreys(job: PersistStoreysJob): Promise<boolean> {
    if (this.isDestroyed || job.epoch !== this.persistEpoch) return false;
    // Suppress flyTo when normalizeDesignLocationAndPoints recenters after this PUT.
    this.locationDragCommitPending = true;
    try {
      await job.handler(job.storeys, job.buildingId, job.reason);
      return true;
    } catch (err) {
      if (isDesignSnapshotSupersededError(err)) {
        // The handler already painted the live storeys. Reverting here would
        // put the cancelled drag back on top of that newer design.
        if (
          job.epoch === this.persistEpoch &&
          job.revision === this.persistRevision
        ) {
          this.locationDragCommitPending = false;
        }
        // The drag's vertex selection belongs to geometry this save did not keep.
        this.eventHandler?.deselectShapesAndPoints();
        return false;
      }
      console.warn('Failed to commit storeys', err);
      if (
        job.epoch === this.persistEpoch &&
        job.revision === this.persistRevision
      ) {
        this.locationDragCommitPending = false;
      }
      if (this.shouldRevertFailedPersist(job)) {
        this.setStoreys(job.originalStoreys, { buildingId: job.buildingId });
      }
      throw err;
    }
  }

  /**
   * True when a failed persist should restore {@link PersistStoreysJob.originalStoreys}.
   * Skip when the map was destroyed, a newer commit superseded this job, or
   * the editor is now bound to a different building.
   */
  private shouldRevertFailedPersist(job: PersistStoreysJob): boolean {
    if (this.isDestroyed || job.epoch !== this.persistEpoch) return false;
    if (job.revision !== this.persistRevision) return false;
    return this.storeysBuildingId === job.buildingId;
  }

  /**
   * Subscribe to draft shape events emitted from the event handler.
   */
  private bindDraftShapeEvents(): void {
    const map = this.mapInstance;
    if (!map) return;

    this.draftInteractionSub?.unsubscribe();

    const subscription = new Subscription();
    const eventHandler = this.events;
    const pointEvents = eventHandler?.pointEvents;
    if (!pointEvents) return;

    const { draft } = pointEvents;
    const draftMouse = draft.mouse;
    const draftKey = draft.key;
    const destroy$ = this.destroy$;

    subscription.add(
      draftKey.escape$
        .pipe(takeUntil(destroy$))
        .subscribe((keyboardEvent: KeyboardEvent) => {
          // Escape cascade: blueprint tool step → cancel wall draft.
          if (this.blueprintUnderlay.handleEscape()) {
            keyboardEvent.preventDefault();
            return;
          }
          const hasDraft = Boolean(this.state.draftShape$.getValue());
          const selections = eventHandler.getSelections();
          const hasGeometrySelection = Boolean(
            selections.shapeId ||
            selections.pointIndexes?.length ||
            selections.draftShape,
          );
          const hasDrawnStorey = [
            ...this.state.storeys$.getValue(),
            ...this.state.otherBuildings$
              .getValue()
              .flatMap((building) => building.storeys),
          ].some((storey) => storey.shapes.length > 0);
          // With no draft, let the window-level tool handler consume a second
          // Escape and leave Draw. An entirely empty project must stay drawable.
          if (
            shouldExitDrawToolOnEscape({
              drawToolActive: this.designMapTool === 'draw',
              hasDraft,
              hasDrawnStorey,
            })
          ) {
            return;
          }
          // No map-local interaction remains: let the window-level handler
          // clear the selected storey/building instead.
          if (!hasDraft && !hasGeometrySelection) return;
          keyboardEvent.preventDefault();
          this.reset();
          // Refresh renderer immediately to clear any draft overlays after reset
          this.renderer?.refreshVertices();
        }),
    );

    subscription.add(
      draftMouse.click$.pipe(takeUntil(destroy$)).subscribe((payload) => {
        const epoch = this.draftClickEpoch;
        this.draftClickQueue = enqueueAsync(this.draftClickQueue, async () => {
          if (epoch !== this.draftClickEpoch) return;
          await this.handleDraftShapeInteraction(payload);
        });
      }),
    );

    this.draftInteractionSub = subscription;
  }

  /**
   * Apply a mutator to the current storeys and update the state (without committing / sending to server).
   * @param mutator
   * @returns
   */
  private updateStoreys(mutator: (storeys: Storey[]) => Storey[]): void {
    const current = this.state.storeys$.getValue();
    const next = mutator(current);
    if (next === current) return;
    this.state.storeys$.next(
      this.clampMezzanineStoreysAfterParentUpdate(current, next),
    );
  }

  /**
   * Clamp mezzanine storeys when their non-mezzanine parent storey changes.
   * @param previous
   * @param next
   * @returns updated storeys with clamped mezzanine shapes
   */
  private clampMezzanineStoreysAfterParentUpdate(
    previous: Storey[],
    next: Storey[],
  ): Storey[] {
    const previousById = new globalThis.Map(
      previous.map((storey) => [storey.id, storey]),
    );
    let updated = next;

    for (const storey of next) {
      const previousStorey = previousById.get(storey.id);
      if (previousStorey === storey) continue;
      if (designGuards.is.mezzanineStorey(storey)) continue;
      // Clamp any mezzanines inside this parent after its geometry updates.
      updated = clampMezzanineStoreysInStorey(updated, storey);
    }

    return updated;
  }

  /** Finish lit selected corners before committing the point drag. */
  private snapPointRightAngleOnRelease(payload: PointPayload): void {
    if (
      payload.isDraft ||
      payload.isLineSegmentDrag ||
      payload.isMetaOrCtrlKeyPressed
    )
      return;
    const selectedIndexes = this.eventHandler?.getSelections().pointIndexes;
    const current = this.state.storeys$.getValue();
    const shape = getItemById(current, payload.storeyId, false)?.shapes.find(
      ({ id }) => id === payload.shapeId,
    );
    if (!shape) return;
    let correctedIndexes: number[];
    if (selectedIndexes && selectedIndexes.length > 1) {
      const plan = planReleasedCornerRightAngles(shape.points, selectedIndexes);
      if (!plan) return;
      correctedIndexes = plan.pointIndexes;
      this.applyPointMoveUpdate(
        payload,
        false,
        new globalThis.Map(
          plan.nextPoints.map((point, index) => [index, point]),
        ),
      );
    } else {
      const plan = planReleasedCornerRightAngle(shape.points, payload.index);
      if (!plan) return;
      correctedIndexes = [payload.index];
      const offset: Point2D = [
        plan.nextPoint[0] - payload.origin[0],
        plan.nextPoint[1] - payload.origin[1],
      ];
      this.applyPointMoveUpdate({
        ...payload,
        position: plan.nextPoint,
        offset,
        distance: Math.hypot(...offset),
      });
    }
    const result = getItemById(
      this.state.storeys$.getValue(),
      payload.storeyId,
      false,
    )?.shapes.find(({ id }) => id === payload.shapeId);
    const retainedAngles =
      result &&
      correctedIndexes.every((index) => {
        const angle = getPolygonCornerAngleDegrees(result.points, index);
        return angle !== undefined && Math.abs(angle - 90) <= 1e-4;
      });
    // A geometry clamp must not turn an exact angle into a partial correction.
    if (!retainedAngles) this.state.storeys$.next(current);
  }

  /**
   * Apply a live point drag update by updating {@link MapStateObservables.storeys$}.
   *
   * Henrik: the map should render from one coherent snapshot (state), then persist on explicit end
   * actions, similar to how Boverket expects a single coherent submission.
   */
  private applyPointMoveUpdate(
    payload: PointPayload,
    collapseShortSegmentOnRelease = false,
    targetPositions?: ReadonlyMap<number, Point2D>,
  ): void {
    const { storeyId, shapeId } = payload;
    if (payload.isDraft) return;
    if (!shapeId) return;

    const currentStoreys = this.state.storeys$.getValue();
    const baseStoreys = this.pointMoveBaseStoreys ?? currentStoreys;
    if (!this.pointMoveBaseStoreys) {
      // Capture once so we avoid cumulative drift while dragging.
      this.pointMoveBaseStoreys = baseStoreys;
    }

    // Derive all move operations (exact-position + line-linked) through one unified path.
    const pointMoves = this.buildPointMoves(
      payload,
      baseStoreys,
      collapseShortSegmentOnRelease,
      targetPositions,
    );

    let updatedStoreys: Storey[] = mutateStoreys.many.points.move(
      baseStoreys,
      ...pointMoves,
    );
    const draggedStorey = updatedStoreys.find(
      (storey) => storey.id === storeyId,
    );

    if (draggedStorey && designGuards.is.mezzanineStorey(draggedStorey)) {
      // Dragging a mezzanine: clamp it inside its parent storey in real time.
      const parentBelow = getStoreyBelow(updatedStoreys, draggedStorey);
      if (parentBelow) {
        updatedStoreys = clampMezzanineStoreysInStorey(
          updatedStoreys,
          parentBelow,
          {
            removeNearbyClampedPoints: false,
          },
        );
      }
    } else if (draggedStorey && !payload.suppressUpwardPropagation) {
      // Dragging a parent storey: clamp its child mezzanines in real time.
      updatedStoreys = clampMezzanineStoreysInStorey(
        updatedStoreys,
        draggedStorey,
        {
          removeNearbyClampedPoints: false,
        },
      );
    }

    // Prevent live-drag from producing self-intersecting mezzanine shapes.
    if (
      this.hasSelfIntersectingChangedMezzanineShape(
        currentStoreys,
        updatedStoreys,
      )
    ) {
      return;
    }

    const draggedBaseShape = baseStoreys
      .find((storey) => storey.id === storeyId)
      ?.shapes.find((shape) => shape.id === shapeId);
    const invalidUniformCutoutOffset = Boolean(
      payload.uniformPolygonOffset &&
      draggedBaseShape?.cutout === true &&
      this.hasInvalidChangedCutoutMargin(baseStoreys, updatedStoreys),
    );

    // Clamp solid edits against enclosed cutouts. Uniform cutout offsets use
    // the same whole-drag ratio so the ring stays parallel instead of having
    // individual vertices pulled away from the pointer.
    if (
      invalidUniformCutoutOffset ||
      this.wouldInvalidateCutouts(storeyId, shapeId, updatedStoreys)
    ) {
      updatedStoreys = this.clampSolidDragToCutoutBoundary(
        payload,
        baseStoreys,
        pointMoves,
        invalidUniformCutoutOffset,
      );
    }

    if (!payload.uniformPolygonOffset) {
      updatedStoreys = this.clampInvalidCutoutVertices(
        baseStoreys,
        updatedStoreys,
      );
    }

    const touchedShapes = new Set(
      pointMoves.map((move) => `${move.storeyId}:${move.shapeId}`),
    );
    const separatesPoints = updatedStoreys.some((storey) =>
      storey.shapes.some((shape) => {
        if (!touchedShapes.has(`${storey.id}:${shape.id}`)) return false;
        const before = baseStoreys
          .find((candidate) => candidate.id === storey.id)
          ?.shapes.find((candidate) => candidate.id === shape.id);
        return dragIncreasesPointSeparation(before?.points ?? [], shape.points);
      }),
    );
    // A flung corner can be dragged home. Refuse only a frame that pushes a
    // moved vertex farther past 5000 m.
    if (separatesPoints) return;

    this.state.storeys$.next(updatedStoreys);
  }

  /**
   * Check whether any changed mezzanine shape became self-intersecting.
   * @param previous Storeys before applying the drag update
   * @param next Storeys after applying the drag update
   * @returns true when a changed mezzanine shape is self-intersecting
   */
  private hasSelfIntersectingChangedMezzanineShape(
    previous: Storey[],
    next: Storey[],
  ): boolean {
    const previousById = new globalThis.Map(
      previous.map((storey) => [storey.id, storey]),
    );

    for (const storey of next) {
      if (!designGuards.is.mezzanineStorey(storey)) continue;
      const previousStorey = previousById.get(storey.id);
      if (previousStorey === storey) continue;

      // Guard against invalid preview states while dragging line segments.
      const hasSelfIntersectingShape = storey.shapes.some((shape) =>
        isPolygonSelfIntersecting(shape.points),
      );
      if (hasSelfIntersectingShape) return true;
    }

    return false;
  }

  /**
   * Check whether applying the updated storeys would make any cutout on the
   * dragged storey violate its enclosing solid margin constraint.
   * Only relevant when a non-cutout (solid) shape is being dragged.
   */
  private wouldInvalidateCutouts(
    storeyId: Storey['id'],
    draggedShapeId: string,
    updatedStoreys: Storey[],
  ): boolean {
    const storey = updatedStoreys.find((s) => s.id === storeyId);
    if (!storey) return false;

    const draggedShape = storey.shapes.find((s) => s.id === draggedShapeId);
    if (draggedShape?.cutout === true) return false;

    return storey.shapes
      .filter((s) => s.cutout === true)
      .some(
        (cutout) =>
          !isCutoutInsideStoreyWithMargin({
            cutout,
            storey,
            minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
          }),
      );
  }

  /**
   * Binary-search for the largest drag ratio that keeps all cutouts inside
   * their enclosing solid. Rebuilds storeys at each candidate ratio by
   * interpolating between baseline and target positions.
   */
  private clampSolidDragToCutoutBoundary(
    payload: PointPayload,
    baseStoreys: Storey[],
    pointMoves: {
      storeyId: Storey['id'];
      shapeId: string;
      pointIndex: number;
      position: Point2D;
    }[],
    validateChangedCutout = false,
  ): Storey[] {
    const { storeyId, shapeId } = payload;

    const basePositions = this.resolveBasePositions(baseStoreys, pointMoves);
    if (basePositions.length === 0) return baseStoreys;

    const buildAtRatio = (t: number): Storey[] => {
      const interpolated = basePositions.map(({ base, target, ...ids }) => ({
        ...ids,
        position: [
          base[0] + (target[0] - base[0]) * t,
          base[1] + (target[1] - base[1]) * t,
        ] as Point2D,
      }));
      return mutateStoreys.many.points.move(baseStoreys, ...interpolated);
    };

    const isValid = (storeys: Storey[]): boolean =>
      validateChangedCutout
        ? !this.hasInvalidChangedCutoutMargin(baseStoreys, storeys)
        : !this.wouldInvalidateCutouts(storeyId, shapeId ?? '', storeys);

    let lo = 0;
    let hi = 1;

    for (let i = 0; i < 16; i++) {
      const mid = (lo + hi) / 2;
      if (isValid(buildAtRatio(mid))) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    // Pull back slightly to avoid landing exactly on the boundary
    const safeRatio = Math.max(0, lo - 0.001);
    return buildAtRatio(safeRatio);
  }

  /**
   * Resolve baseline and target positions for all points that would move
   * in the current drag operation.
   */
  private resolveBasePositions(
    baseStoreys: Storey[],
    pointMoves: {
      storeyId: Storey['id'];
      shapeId: string;
      pointIndex: number;
      position: Point2D;
    }[],
  ): {
    storeyId: Storey['id'];
    shapeId: string;
    pointIndex: number;
    base: Point2D;
    target: Point2D;
  }[] {
    return pointMoves.flatMap((move) => {
      const baseStorey = baseStoreys.find((s) => s.id === move.storeyId);
      const baseShape = baseStorey?.shapes.find((s) => s.id === move.shapeId);
      const basePoint = baseShape?.points[move.pointIndex];
      if (!basePoint) return [];
      return [
        {
          storeyId: move.storeyId,
          shapeId: move.shapeId,
          pointIndex: move.pointIndex,
          base: basePoint,
          target: move.position,
        },
      ];
    });
  }

  /**
   * Build move operations for dragged points (single or multi-select) using the drag offset.
   * Resolves both exact-position linked points and line-linked points on upper storeys.
   */
  private buildPointMoves(
    payload: PointPayload,
    baseStoreys: Storey[],
    collapseShortSegmentOnRelease = false,
    targetPositions?: ReadonlyMap<number, Point2D>,
  ): PointMove[] {
    const { storeyId, shapeId, index: pointIndex } = payload;
    if (!shapeId) return [];

    return buildPointMoves({
      baseStoreys,
      storeyId,
      shapeId,
      pointIndex,
      offset: payload.offset,
      targetPositions,
      selectedPointIndexes: this.eventHandler
        ?.getSelectionObservables()
        .pointIndexes$.getValue(),
      lineSegmentPointIndexes: payload.lineSegmentPointIndexes,
      isLineSegmentDrag: payload.isLineSegmentDrag,
      collapseShortSegmentOnRelease,
      uniformPolygonOffset: payload.uniformPolygonOffset,
      segmentTranslationSnapMeters: payload.segmentTranslationSnapMeters,
      suppressUpwardPropagation: payload.suppressUpwardPropagation,
    });
  }

  /** True when a released segment drag should merge its two endpoints. */
  private shouldCollapseLineSegmentOnRelease(payload: PointPayload): boolean {
    if (
      !payload.isLineSegmentDrag ||
      payload.uniformPolygonOffset ||
      !payload.shapeId ||
      payload.isDraft
    ) {
      return false;
    }
    const baseStoreys =
      this.pointMoveBaseStoreys ?? this.state.storeys$.getValue();
    const baseStorey = baseStoreys.find(
      (storey) => storey.id === payload.storeyId,
    );
    const baseShape = baseStorey?.shapes.find(
      (shape) => shape.id === payload.shapeId,
    );
    if (!baseShape) return false;
    return shouldCollapseLineSegmentDrag(
      baseShape.points,
      payload.index,
      payload.offset,
      payload.segmentTranslationSnapMeters,
    );
  }

  /**
   * Resolve all storey/shape targets affected by an insert-drag.
   */
  private resolveInsertDragTargets(
    payload: PointPayload,
  ): { storeyId: Storey['id']; shapeId: string; insertIndex: number }[] {
    const baseShapeId =
      payload.shapeId ??
      (typeof payload.shape.id === 'string' ? payload.shape.id : undefined);
    if (!baseShapeId) return [];

    const storeys = this.state.storeys$.getValue();
    const byStorey = new globalThis.Map<
      Storey['id'],
      { storeyId: Storey['id']; shapeId: string; insertIndex: number }
    >();

    // Base storey target.
    byStorey.set(payload.storeyId, {
      storeyId: payload.storeyId,
      shapeId: baseShapeId,
      insertIndex: payload.index,
    });

    // Linked (upper) storeys, resolved via level -> storeyId.
    for (const level of payload.linkedStoreyLevels ?? []) {
      const storeyId = storeys[level]?.id;
      if (!storeyId) continue;
      const shapeId = payload.linkedShapeIds?.[level] ?? baseShapeId;
      const insertIndex = payload.linkedInsertIndexes?.[level] ?? payload.index;
      byStorey.set(storeyId, { storeyId, shapeId, insertIndex });
    }

    return Array.from(byStorey.values());
  }

  /**
   * Insert the new vertex into {@link MapStateObservables.storeys$} at drag start.
   * Subsequent updates move the inserted vertex like a standard point move.
   */
  private applyPointInsertStart(payload: PointPayload): void {
    if (!payload.isInsertDrag) return;
    const targets = this.resolveInsertDragTargets(payload);
    if (!targets.length) return;

    // Henrik: store the resolved targets so update uses the same set deterministically.
    this.insertDragTargets = targets.map((t) => ({
      storeyId: t.storeyId,
      shapeId: t.shapeId,
      pointIndex: t.insertIndex,
    }));

    const byStorey = new globalThis.Map<Storey['id'], typeof targets>();
    targets.forEach((target) => {
      const existing = byStorey.get(target.storeyId) ?? [];
      existing.push(target);
      byStorey.set(target.storeyId, existing);
    });

    this.updateStoreys((storeys) => {
      let mutated = false;
      const nextStoreys = storeys.map((storey) => {
        const storeyTargets = byStorey.get(storey.id);
        if (!storeyTargets?.length) return storey;

        let storeyMutated = false;
        const nextShapes = storey.shapes.map((shape) => {
          const target = storeyTargets.find((t) => t.shapeId === shape.id);
          if (!target) return shape;
          if (!shape.points?.length) return shape;
          const nextShape = mutateShape.point.insertAt(
            shape,
            target.insertIndex,
            payload.origin,
          );
          if (nextShape === shape) return shape;
          storeyMutated = true;
          return nextShape;
        });
        if (!storeyMutated) return storey;
        mutated = true;
        return { ...storey, shapes: nextShapes };
      });
      return mutated ? nextStoreys : storeys;
    });
  }

  /**
   * Remove the provisional insert point when an insert-drag is ignored.
   */
  private removeInsertDragPoints(): void {
    const targets = this.insertDragTargets;
    if (!targets?.length) return;

    const targetsByStorey = new globalThis.Map<Storey['id'], typeof targets>();
    targets.forEach((target) => {
      const existing = targetsByStorey.get(target.storeyId) ?? [];
      existing.push(target);
      targetsByStorey.set(target.storeyId, existing);
    });

    this.updateStoreys((storeys) => {
      let mutated = false;
      const nextStoreys = storeys.map((storey) => {
        const storeyTargets = targetsByStorey.get(storey.id);
        if (!storeyTargets?.length) return storey;

        let storeyMutated = false;
        const nextShapes = storey.shapes.map((shape) => {
          const target = storeyTargets.find(
            (entry) => entry.shapeId === shape.id,
          );
          if (!target) return shape;
          if ((shape.points?.length ?? 0) <= 1) return shape;
          // Henrik: remove the provisional point to restore pre-insert geometry.
          const nextShape = mutateShape.point.remove(shape, target.pointIndex);
          if (nextShape === shape) return shape;
          storeyMutated = true;
          return nextShape;
        });

        if (!storeyMutated) return storey;
        mutated = true;
        return { ...storey, shapes: nextShapes };
      });

      return mutated ? nextStoreys : storeys;
    });
  }

  /**
   * Select the point that was just created by an add-point drag so it appears marked.
   * Uses current storeys after commit to resolve the shape.
   */
  private selectNewlyInsertedPoint(payload: PointPayload): void {
    const { storeyId, shapeId, index } = payload;
    if (storeyId == null || shapeId == null || index == null) return;
    const storeys = this.state.storeys$.getValue();
    const storey = getStoreyById(storeys, storeyId);
    const shape = storey?.shapes.find((s) => s.id === shapeId);
    if (shape) {
      this.eventHandler?.selectPoints(shape, [index]);
    }
  }

  /** Select the vertex following one that was just removed from a polygon. */
  private selectNextPointAfterRemoval(
    shapeId: Shape['id'],
    removedIndex: number,
  ): void {
    const storeys = this.state.storeys$.getValue();
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    const selectedStorey = selectedStoreyId
      ? getStoreyById(storeys, selectedStoreyId)
      : undefined;
    const shape =
      selectedStorey?.shapes.find((candidate) => candidate.id === shapeId) ??
      storeys
        .flatMap((storey) => storey.shapes)
        .find((candidate) => candidate.id === shapeId);
    const nextIndex = shape
      ? getNextPointIndexAfterRemoval(removedIndex, shape.points.length)
      : undefined;
    if (!shape || nextIndex === undefined) {
      this.eventHandler?.deselectShapesAndPoints();
      return;
    }
    this.eventHandler?.selectPoints(shape, [nextIndex]);
  }

  /**
   * Resolve the point position for a shape/id pair from the current storeys.
   */
  private resolvePointPosition(
    shapeId: Shape['id'],
    pointIndex: number,
  ): Point2D | undefined {
    for (const storey of this.state.storeys$.getValue()) {
      // Scan current storeys to find the active point position.
      const shape = storey.shapes.find((candidate) => candidate.id === shapeId);
      const point = shape?.points?.[pointIndex];
      if (point) return point;
    }
    return undefined;
  }

  /**
   * Convert a finalized triangle to a draft line by removing one point. If upper storeys exist these will be removed.
   * As for now this is intended behaviour since user can abort with ESC.
   * Henrik would rather keep editing flow continuous than blocking deletion.
   */
  private tryEnterDraftModeOnPointRemoval({
    shapeId,
    pointIndex,
    position,
  }: {
    shapeId?: Shape['id'];
    pointIndex?: number;
    position?: Point2D;
  }): boolean {
    const selectedStoreyId = this.state.selectedStoreyId$.getValue();
    if (!selectedStoreyId) return false;
    const storeys = this.state.storeys$.getValue();
    const selectedStorey = getStoreyById(storeys, selectedStoreyId);
    if (!selectedStorey?.shapes?.length) return false;
    const selectedStoreyIndex = getStoreyIndex(storeys, selectedStorey);
    if (selectedStoreyIndex < 0) return false;

    const shape =
      (shapeId
        ? selectedStorey.shapes.find((candidate) => candidate.id === shapeId)
        : undefined) ??
      selectedStorey.shapes.find((candidate) => {
        if (candidate.points.length !== 3 || !position) return false;
        return candidate.points.some(
          (point) => point[0] === position[0] && point[1] === position[1],
        );
      });
    if (!shape || shape.points.length !== 3) return false;

    const removalIndex =
      typeof pointIndex === 'number'
        ? pointIndex
        : position
          ? shape.points.findIndex(
              (point) => point[0] === position[0] && point[1] === position[1],
            )
          : -1;
    if (removalIndex < 0 || removalIndex >= shape.points.length) return false;

    const draftPoints = shape.points.filter(
      (_, index) => index !== removalIndex,
    );
    const storeyIdsAbove = storeys
      .slice(selectedStoreyIndex + 1)
      .map((storey) => storey.id);
    this.draftPropagationStoreyIds = storeyIdsAbove;

    this.updateStoreys((currentStoreys) =>
      currentStoreys.map((storey) =>
        storey.id === selectedStoreyId
          ? {
              ...storey,
              shapes: storey.shapes.filter(
                (candidate) => candidate.id !== shape.id,
              ),
            }
          : storeyIdsAbove.includes(storey.id)
            ? { ...storey, shapes: [] }
            : storey,
      ),
    );
    // Keep shape id stable so interaction state stays predictable during drafting.
    this.state.draftShape$.next(
      createDraftShape({
        id: shape.id,
        points: draftPoints,
        cutout: shape.cutout === true,
      }),
    );
    this.eventHandler?.deselectShapesAndPoints();
    return true;
  }

  /**
   * Remove all points at a position across all storeys without moving anything.
   */
  private applyPointRemoveAtPosition(position: Point2D): void {
    this.updateStoreys((storeys) => {
      // Henrik: use design-level mutator to remove all points at a position.
      const design: BuildingDesign = {
        location: this.state.location$.getValue(),
        activities: [],
        buildings: [createDesignBuilding(storeys)],
      };
      const selectedStoreyId = this.state.selectedStoreyId$.getValue();
      const selectedStorey = selectedStoreyId
        ? getStoreyById(storeys, selectedStoreyId)
        : undefined;
      const nextDesign = mutateStoreyInDesign.many.points.removeAt(
        design,
        position,
        selectedStorey ? { deleteInStoreysAbove: selectedStorey } : undefined,
      );
      const nextStoreys = getStoreys(nextDesign);
      return nextStoreys === storeys ? storeys : nextStoreys;
    });
  }

  /**
   * Apply shape translation locally across all targets.
   */
  private applyShapeTranslate(payload: ShapeTranslateEvent): void {
    const { targets } = payload;
    if (!targets?.length) return;
    // Henrik: center snap for cutouts runs in the event handler; end payload delta is already final.
    const delta = payload.delta;
    const [dx, dy] = delta;
    const rotation = payload.rotation;
    if (!dx && !dy && !rotation?.radians) return;

    const byStorey = new globalThis.Map<string, Set<string>>();
    targets.forEach(({ storeyId, shapeIds }) => {
      if (!storeyId || !shapeIds?.length) return;
      const current = byStorey.get(storeyId) ?? new Set<string>();
      shapeIds.forEach((id) => current.add(id));
      byStorey.set(storeyId, current);
    });
    if (!byStorey.size) return;

    this.updateStoreys((storeys) => {
      let mutated = false;
      const next = storeys.map((storey) => {
        const targetSet = byStorey.get(storey.id);
        if (!targetSet || !targetSet.size) return storey;
        let storeyMutated = false;
        const shapes = storey.shapes.map((shape) => {
          if (!targetSet.has(shape.id)) return shape;
          if (!shape.points?.length) return shape;
          const translatedShape =
            dx || dy ? mutateShape.point.translateMany(shape, delta) : shape;
          const nextShape = rotation?.radians
            ? {
                ...translatedShape,
                points: rotatePolygonAroundCenter(
                  translatedShape.points,
                  rotation.center,
                  rotation.radians,
                ),
              }
            : translatedShape;
          if (nextShape === shape) return shape;
          storeyMutated = true;
          return nextShape;
        });
        if (!storeyMutated) return storey;
        mutated = true;
        return { ...storey, shapes };
      });
      if (mutated && this.hasInvalidChangedCutoutMargin(storeys, next)) {
        return storeys;
      }
      return mutated ? next : storeys;
    });
  }

  /**
   * Apply a local origin rebase so persist cannot paint a new pin against
   * unrebased footprints.
   */
  private applyRebasedOrigin(result: RebaseMapOriginResult): void {
    if (!result.didChange) return;
    this.locationDragCommitPending = true;
    this.state.location$.next(result.location);
    this.blueprintUnderlay.setLocation(result.location);
    this.state.storeys$.next(result.storeys);
    this.state.otherBuildings$.next(result.otherBuildings);
    const nextUnderlay = result.blueprintUnderlays?.[0];
    if (nextUnderlay) this.blueprintUnderlay.setUnderlay(nextUnderlay);
  }

  /**
   * Check if any changed cutout shape violates the 1m margin constraint.
   * @param previous
   * @param next
   * @returns true when an edited cutout no longer satisfies margin constraints
   */
  private hasInvalidChangedCutoutMargin(
    previous: Storey[],
    next: Storey[],
  ): boolean {
    const previousByStoreyId = new globalThis.Map(
      previous.map((storey) => [storey.id, storey]),
    );
    for (const nextStorey of next) {
      const previousStorey = previousByStoreyId.get(nextStorey.id);
      if (!previousStorey) continue;
      const previousShapeById = new globalThis.Map(
        previousStorey.shapes.map((shape) => [shape.id, shape]),
      );
      for (const shape of nextStorey.shapes) {
        if (shape.cutout !== true) continue;
        const previousShape = previousShapeById.get(shape.id);
        if (!previousShape) continue;
        if (this.areShapePointsEqual(previousShape, shape)) continue;
        const isValid = isCutoutInsideStoreyWithMargin({
          cutout: shape,
          storey: nextStorey,
          minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
        });
        if (!isValid) return true;
      }
    }
    return false;
  }

  /**
   * Clamp every cutout vertex that violates margin back to the nearest valid
   * position. Unlike the old guard (which rejected the entire update), this
   * corrects individual vertices so the drag keeps tracking the pointer.
   *
   * Handles two cases:
   * - Cutout itself was dragged → search from baseline vertex to target vertex.
   * - Enclosing solid was dragged → cutout unchanged, but the margin boundary
   *   moved inward. Fall back to pulling each cutout vertex toward its own
   *   current position (i.e. keep it where it is as long as valid, or pull
   *   inward to the nearest valid spot).
   *
   * @param baseline Storeys at drag start (origin positions)
   * @param next Storeys after applying the pointer move
   * @returns Storeys with any offending cutout vertices pulled inward
   */
  private clampInvalidCutoutVertices(
    baseline: Storey[],
    next: Storey[],
  ): Storey[] {
    const baselineByStoreyId = new globalThis.Map(
      baseline.map((storey) => [storey.id, storey]),
    );
    let mutated = false;
    const result = next.map((nextStorey) => {
      const baselineStorey = baselineByStoreyId.get(nextStorey.id);
      if (!baselineStorey) return nextStorey;
      const baselineShapeById = new globalThis.Map(
        baselineStorey.shapes.map((shape) => [shape.id, shape]),
      );
      let storeyMutated = false;
      const shapes = nextStorey.shapes.map((shape) => {
        if (shape.cutout !== true) return shape;
        if (
          isCutoutInsideStoreyWithMargin({
            cutout: shape,
            storey: nextStorey,
            minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
          })
        ) {
          return shape;
        }
        const baselineShape = baselineShapeById.get(shape.id);
        const cutoutChanged =
          baselineShape && !this.areShapePointsEqual(baselineShape, shape);
        let clampedPoints = shape.points;
        for (let vi = 0; vi < shape.points.length; vi += 1) {
          const nextPoint = clampedPoints[vi];
          if (!nextPoint) continue;
          const baseVertex = cutoutChanged
            ? baselineShape?.points[vi]
            : undefined;
          const originVertex: Point2D = baseVertex ?? nextPoint;
          const currentShape: Shape = { ...shape, points: clampedPoints };
          const currentStorey: Storey = {
            ...nextStorey,
            shapes: nextStorey.shapes.map((s) =>
              s.id === shape.id ? currentShape : s,
            ),
          };
          const clamped = resolveClampedCutoutVertexPosition({
            storey: currentStorey,
            cutout: currentShape,
            vertexIndex: vi,
            currentVertex: originVertex,
            targetVertex: nextPoint,
            minMarginMeters: CUTOUT_MIN_MARGIN_METERS,
          });
          if (clamped[0] !== nextPoint[0] || clamped[1] !== nextPoint[1]) {
            clampedPoints = clampedPoints.map((p, i) =>
              i === vi ? clamped : p,
            ) as Polygon2D;
          }
        }
        if (clampedPoints !== shape.points) {
          storeyMutated = true;
          return { ...shape, points: clampedPoints };
        }
        return shape;
      });
      if (storeyMutated) {
        mutated = true;
        return { ...nextStorey, shapes };
      }
      return nextStorey;
    });
    return mutated ? result : next;
  }

  /**
   * Check if two shapes have identical point coordinates.
   * @param first
   * @param second
   * @returns true when all points match in order
   */
  private areShapePointsEqual(first: Shape, second: Shape): boolean {
    if (first.points.length !== second.points.length) return false;
    for (let index = 0; index < first.points.length; index += 1) {
      const firstPoint = first.points[index];
      const secondPoint = second.points[index];
      if (!firstPoint || !secondPoint) return false;
      if (
        firstPoint[0] !== secondPoint[0] ||
        firstPoint[1] !== secondPoint[1]
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * Remove base + intersecting shapes locally to keep storey layers consistent.
   */
  private applyShapeRemove(payload: ShapeRemoveEvent): void {
    const { shapeId, storeyId } = payload;
    if (!shapeId || !storeyId) return;

    const storeys = this.state.storeys$.getValue();
    const baseIndex = storeys.findIndex(
      (candidate) => candidate.id === storeyId,
    );
    if (baseIndex < 0) return;
    const baseStorey = storeys[baseIndex];
    if (!baseStorey?.shapes?.length) return;
    const removedShape = baseStorey.shapes.find(
      (candidate) => candidate.id === shapeId,
    );
    if (!removedShape) return;

    const toRemoveByStorey = new globalThis.Map<string, Set<string>>();
    const mergeIntersecting = (
      target: globalThis.Map<string, Set<string>>,
      byStorey: Record<string, Shape[]>,
    ) => {
      Object.entries(byStorey).forEach(([candidateStoreyId, shapes]) => {
        const set = target.get(candidateStoreyId) ?? new Set<string>();
        shapes.forEach((shape) => set.add(shape.id));
        target.set(candidateStoreyId, set);
      });
    };

    const enclosedCutouts =
      removedShape.cutout !== true
        ? getCutoutsEnclosedBySolid(baseStorey, removedShape)
        : [];

    const baseRemoveIds = new Set<string>([removedShape.id]);
    for (const c of enclosedCutouts) {
      baseRemoveIds.add(c.id);
    }
    toRemoveByStorey.set(baseStorey.id, baseRemoveIds);
    const cutoutSeeds: { storeyIndex: number; cutout: Shape }[] =
      removedShape.cutout === true
        ? [{ storeyIndex: baseIndex, cutout: removedShape }]
        : enclosedCutouts.map((cutout) => ({
            storeyIndex: baseIndex,
            cutout,
          }));
    if (cutoutSeeds.length) {
      mergeIntersecting(
        toRemoveByStorey,
        collectTransitiveLinkedCutoutsAbove(storeys, cutoutSeeds),
      );
    }

    if (removedShape.cutout !== true) {
      mergeIntersecting(
        toRemoveByStorey,
        getIntersectingShapesByStorey(removedShape, storeys, baseIndex),
      );
    }

    // Courtyard houses and nested holes sit inside the removed cutout.
    for (const storey of storeys) {
      const removeSet = toRemoveByStorey.get(storey.id);
      if (!removeSet?.size) continue;
      const removedCutouts = storey.shapes.filter(
        (shape) => removeSet.has(shape.id) && shape.cutout === true,
      );
      for (const cutout of removedCutouts) {
        for (const inside of getShapesContainedInCutout(storey, cutout)) {
          removeSet.add(inside.id);
        }
      }
    }

    this.updateStoreys((currentStoreys) => {
      let mutated = false;
      const next = currentStoreys.map((storey) => {
        const removeSet = toRemoveByStorey.get(storey.id);
        if (!removeSet?.size) return storey;
        const nextShapes = storey.shapes.filter(
          (shape) => !removeSet.has(shape.id),
        );
        if (nextShapes.length === storey.shapes.length) return storey;
        mutated = true;
        return { ...storey, shapes: nextShapes };
      });
      return mutated ? next : currentStoreys;
    });
  }

  /**
   * Set the perspective mode (2D or 3D) and lock/unlock rotation accordingly.
   * Animates the transition smoothly.
   * @param mode '2d' for top-down view (pitch 0), '3d' for perspective view (pitch 60)
   */
  setPerspectiveMode(mode: '2d' | '3d'): void {
    const map = this.mapInstance;
    if (!map || !this.loaded) return;

    this.setPerspectiveModeIntent(mode);
    setMapPerspectiveMode(map, mode);
    // Pitch animates; also re-sync on moveend. Apply immediately for the mode intent.
    this.syncBlueprintUnderlayPresentation();
  }

  /**
   * Sync 2D/3D intent (soft-grey guides) without animating the camera.
   * Used when restoring session view mode from the Design store.
   */
  setPerspectiveModeIntent(mode: ViewMode): void {
    if (this.perspectiveMode === mode) return;
    this.perspectiveMode = mode;
    this.threeJs?.setSoftenGuidesBelowSelected(mode === '2d');
  }

  /**
   * Read the current Mapbox camera for Design tab session persistence.
   */
  getCameraState(): DesignMapCameraState | undefined {
    const map = this.mapInstance;
    if (!map || !this.loaded) return undefined;
    const center = map.getCenter();
    const padding = map.getPadding();
    return {
      center: [center.lng, center.lat],
      zoom: map.getZoom(),
      pitch: map.getPitch(),
      bearing: map.getBearing(),
      padding: {
        top: padding.top ?? 0,
        right: padding.right ?? 0,
        bottom: padding.bottom ?? 0,
        left: padding.left ?? 0,
      },
      facadeModeActive: this.facadeModeActive,
      facadeTransitionProgress: this.facadeTransitionProgress,
      facadeVerticalOffsetPixels: this.facadeVerticalOffsetPixels,
    };
  }

  /** Restore the Three.js-only portion of a persisted Design camera. */
  restoreFacadeCameraState(camera: DesignMapCameraState): void {
    this.facadeVerticalOffsetPixels = camera.facadeVerticalOffsetPixels ?? 0;
    if (!camera.facadeModeActive) return;
    this.setFacadeModeActive(true);
    this.threeJs?.setFacadeVerticalOffsetPixels(
      this.facadeVerticalOffsetPixels,
    );
    this.applyFacadeTransitionProgress(camera.facadeTransitionProgress ?? 1);
    this.pitchTransitionActive = false;
    this.syncVerticalFovForPitch();
    this.mapInstance?.triggerRepaint();
  }

  /** Return to Mapbox's projection before animating to the project list. */
  exitFacadeMode(): void {
    this.setFacadeModeActive(false);
    this.facadeVerticalOffsetPixels = 0;
  }

  /**
   * Register a handler invoked on `moveend` (and once more on detach) so React
   * can remember zoom/angle/center across Design ↔ Build remounts.
   */
  setCameraChangeHandler(
    handler: ((camera: DesignMapCameraState) => void) | undefined,
  ): void {
    this.cameraChangeHandler = handler;
  }

  private attachCameraChangeListener(map: Map): void {
    if (this.cameraMoveListenerAttached) return;
    map.on('moveend', this.handleCameraMoveEnd);
    // `rotate` fires continuously while turning, so a compass can follow live.
    map.on('rotate', this.handleMapRotate);
    map.on('pitchstart', this.handleMapPitchStart);
    map.on('pitch', this.handleMapPitch);
    map.on('pitchend', this.handleMapPitchEnd);
    this.cameraMoveListenerAttached = true;
  }

  /**
   * Register a handler that receives the map bearing in degrees whenever the
   * map rotates. Called immediately with the current bearing.
   * @param handler Receives the bearing, or undefined to unsubscribe
   */
  setBearingChangeHandler(handler: ((bearing: number) => void) | undefined) {
    this.bearingChangeHandler = handler;
    const bearing = this.mapInstance?.getBearing();
    if (handler && bearing !== undefined) handler(bearing);
  }

  /** Align and center the selected building, or the complete unselected site. */
  alignBearingToBuildingAxes(rightViewportInsetPx = 0): boolean {
    const map = this.mapInstance;
    if (!map) return false;
    const hasBuildingSelection = this.state.hasBuildingSelection$.getValue();
    const selectedStoreys = this.state.storeys$.getValue();
    const otherBuildings = this.state.otherBuildings$.getValue();
    const directions = getRankedBearingDirectionsForSelection({
      hasBuildingSelection,
      selectedStoreys,
      otherBuildings,
    });
    const alignment = getNextBuildingBearingAlignment({
      rawBearing: map.getBearing(),
      rankedDirections: directions,
    });
    if (!alignment) return false;
    const storeyGroupsToCenter = hasBuildingSelection
      ? [selectedStoreys]
      : otherBuildings.map((building) => building.storeys);
    const storeysToCenter = storeyGroupsToCenter.flat();
    const centerMetric = getBuildingRotationCenter(storeysToCenter);
    const centerCoordinate = centerMetric
      ? convertMetricOffsetToCoordinate(
          this.state.location$.getValue(),
          centerMetric,
        )
      : undefined;
    const verticalCenterOffset = centerCoordinate
      ? getBuildingElevationCenterOffset({
          map,
          center: centerCoordinate,
          storeyGroups: storeyGroupsToCenter,
        })
      : 0;
    map.easeTo({
      bearing: alignment.bearing,
      ...(centerCoordinate && {
        center: [centerCoordinate.lng, centerCoordinate.lat] as [
          number,
          number,
        ],
        offset: [0, verticalCenterOffset],
        padding: {
          top: 0,
          bottom: 0,
          left: getMapViewportLeftInset(map),
          right: Math.max(0, rightViewportInsetPx),
        },
        // Padding moves Mapbox's projection center / vanishing point. Keep it
        // after the ease so perspective stays centered in the visible canvas.
        retainPadding: true,
      }),
      duration: QUICK_CAMERA_ANIMATION_DURATION_MS,
    });
    return true;
  }

  /** Remove right-panel padding while retaining the visible map's left edge. */
  clearViewportRightInset(): void {
    const map = this.mapInstance;
    if (!map) return;
    map.setPadding({
      top: 0,
      bottom: 0,
      left: getMapViewportLeftInset(map),
      right: 0,
    });
  }

  /** Frame the selected building, or the complete site, inside the visible map. */
  zoomToBuildingSelection(rightViewportInsetPx = 0): boolean {
    const map = this.mapInstance;
    if (!map) return false;
    const hasBuildingSelection = this.state.hasBuildingSelection$.getValue();
    const selectedStoreys = this.state.storeys$.getValue();
    const otherBuildings = this.state.otherBuildings$.getValue();
    const storeyGroups = hasBuildingSelection
      ? [selectedStoreys]
      : otherBuildings.map((building) => building.storeys);
    const marginFraction =
      BUILDING_VOLUME_FOCUS_MARGIN_CYCLE[
        this.buildingVolumeFocusMarginIndex %
          BUILDING_VOLUME_FOCUS_MARGIN_CYCLE.length
      ] ?? BUILDING_VOLUME_FOCUS_MARGIN_CYCLE[0];
    const camera = getBuildingVolumeFocusCamera({
      map,
      location: this.state.location$.getValue(),
      storeyGroups,
      rightViewportInsetPx,
      marginFraction,
    });
    if (!camera) return false;

    map.easeTo({
      center: camera.center,
      zoom: camera.zoom,
      offset: camera.offset,
      padding: camera.padding,
      retainPadding: true,
      duration: QUICK_CAMERA_ANIMATION_DURATION_MS,
    });
    this.buildingVolumeFocusMarginIndex =
      (this.buildingVolumeFocusMarginIndex + 1) %
      BUILDING_VOLUME_FOCUS_MARGIN_CYCLE.length;
    return true;
  }

  /** Toggle Shift+T between top-down and a steep 70° pitch. */
  toggleQuickPitch(rightViewportInsetPx = 0): boolean {
    const map = this.mapInstance;
    if (!map) return false;
    const requestedPitch = getShiftTPitchTarget(map.getPitch());
    const hasBuildingSelection = this.state.hasBuildingSelection$.getValue();
    const returnCamera =
      requestedPitch !== 0 ? this.quickPitchReturnCamera : undefined;
    const pitch = returnCamera?.pitch ?? requestedPitch;
    if (requestedPitch === 0) {
      const center = map.getCenter();
      this.quickPitchReturnCamera = {
        center: [center.lng, center.lat],
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
        padding: map.getPadding(),
      };
    }
    const selectedStoreys = this.state.storeys$.getValue();
    const centerMetric = hasBuildingSelection
      ? getBuildingRotationCenter(selectedStoreys)
      : undefined;
    const centerCoordinate = centerMetric
      ? convertMetricOffsetToCoordinate(
          this.state.location$.getValue(),
          centerMetric,
        )
      : undefined;
    const centerOffset =
      centerMetric && centerCoordinate
        ? getBuildingTargetPitchCenterOffset({
            storeys: selectedStoreys,
            center: centerMetric,
            latitudeDegrees: centerCoordinate.lat,
            zoom: map.getZoom(),
            bearingDegrees: map.getBearing(),
            pitchDegrees: pitch,
          })
        : undefined;
    this.setPerspectiveModeIntent(pitch === 0 ? '2d' : '3d');
    this.syncBlueprintUnderlayPresentation();
    // Changing FOV during an ease interrupts Mapbox at the 1° threshold.
    // Restore the perspective lens before leaving top-down; entering the
    // isometric lens waits until pitchend/moveend at exact 0°.
    if (pitch !== 0) {
      // setVerticalFieldOfView emits moveend while pitch is still 0°. Mark the
      // upcoming transition first so that event cannot restore isometric FOV.
      this.pitchTransitionActive = true;
      this.applyVerticalFov(getMapFovForPitch(pitch));
    }
    map.easeTo({
      pitch,
      ...(returnCamera
        ? {
            center: returnCamera.center,
            zoom: returnCamera.zoom,
            bearing: returnCamera.bearing,
            padding: returnCamera.padding,
            retainPadding: true,
          }
        : centerCoordinate && centerOffset
          ? {
              center: [centerCoordinate.lng, centerCoordinate.lat] as [
                number,
                number,
              ],
              offset: centerOffset,
              padding: {
                top: 0,
                bottom: 0,
                left: getMapViewportLeftInset(map),
                right: Math.max(0, rightViewportInsetPx),
              },
              retainPadding: true,
            }
          : {}),
      duration: QUICK_CAMERA_ANIMATION_DURATION_MS,
    });
    if (returnCamera) this.quickPitchReturnCamera = undefined;
    return true;
  }

  /** Enter the Shift+T top-down state without toggling back out of it. */
  enterQuickTopDown(rightViewportInsetPx = 0): boolean {
    const map = this.mapInstance;
    if (!map || getShiftTPitchTarget(map.getPitch()) !== 0) return false;
    return this.toggleQuickPitch(rightViewportInsetPx);
  }

  private readonly handleCameraMoveEnd = (): void => {
    if (this.verticalFovUpdateActive) return;
    if (!this.pitchTransitionActive) this.syncVerticalFovForPitch();
    const camera = this.getCameraState();
    if (!camera) return;
    this.cameraChangeHandler?.(camera);
    this.bearingChangeHandler?.(camera.bearing);
    this.syncBlueprintUnderlayPresentation();
  };

  private notifyCameraChange(): void {
    const camera = this.getCameraState();
    if (camera) this.cameraChangeHandler?.(camera);
  }

  private readonly handleMapRotate = (): void => {
    const bearing = this.mapInstance?.getBearing();
    if (bearing === undefined) return;
    this.bearingChangeHandler?.(bearing);
  };

  private readonly handleMapPitchStart = (): void => {
    if (
      this.facadeModeActive ||
      this.facadeTransitionAnimationFrame !== undefined
    ) {
      return;
    }
    // Restore perspective before the pitch changes. Updating FOV while Mapbox
    // is pitching can interrupt the gesture/ease at the top-down threshold.
    this.pitchTransitionActive = true;
    this.applyVerticalFov(DEFAULT_MAP_FOV);
  };

  private readonly handleMapPitch = (): void => {
    this.syncBasemapCoverForPitch();
  };

  private readonly handleMapPitchEnd = (): void => {
    if (
      this.facadeModeActive ||
      this.facadeTransitionAnimationFrame !== undefined
    ) {
      return;
    }
    this.pitchTransitionActive = false;
    this.syncVerticalFovForPitch();
  };

  /** Keep the lens orthographic-like only while the camera is near top-down. */
  private syncVerticalFovForPitch(): void {
    const map = this.mapInstance;
    if (!map) return;
    this.applyVerticalFov(
      this.facadeModeActive
        ? ISOMETRIC_MAP_FOV
        : getMapFovForPitch(map.getPitch()),
    );
  }

  private setFacadeModeActive(active: boolean): void {
    if (this.facadeModeActive === active) return;
    if (this.facadeTransitionAnimationFrame !== undefined) {
      cancelAnimationFrame(this.facadeTransitionAnimationFrame);
      this.facadeTransitionAnimationFrame = undefined;
    }

    this.facadeModeActive = active;
    this.syncBlueprintUnderlayPresentation();
    this.mouseNavigation.setFacadeModeActive(active);
    if (active) {
      this.pitchTransitionActive = true;
      this.events?.setFacadeMode(true);
      this.threeJs?.setFacadeMode(true);
      this.threeJs?.setFacadeVerticalOffsetPixels(
        this.facadeVerticalOffsetPixels,
      );
      this.syncLocationPinOverlays();
      return;
    }

    this.applyFacadeTransitionProgress(0);
    this.pitchTransitionActive = false;
    this.events?.setFacadeMode(false);
    this.threeJs?.setFacadeMode(false);
    this.threeJs?.setFacadeVerticalOffsetPixels(0);
    this.syncLocationPinOverlays();
    this.syncVerticalFovForPitch();
  }

  /** Follow pointer motion directly between Mapbox's 85° cap and 90°. */
  private setFacadeTransitionProgressFromGesture(progress: number): void {
    if (!this.facadeModeActive) return;
    if (this.facadeTransitionAnimationFrame !== undefined) {
      cancelAnimationFrame(this.facadeTransitionAnimationFrame);
      this.facadeTransitionAnimationFrame = undefined;
    }
    this.pitchTransitionActive = true;
    this.applyFacadeTransitionProgress(progress);
  }

  /** Complete only the remaining facade distance after the pointer is released. */
  private finishFacadeTransition(): void {
    if (!this.facadeModeActive) return;
    this.animateFacadeTransition(1);
  }

  /** Ease the custom projection and lens with the shared quick camera timing. */
  private animateFacadeTransition(targetProgress: 0 | 1): void {
    if (this.facadeTransitionAnimationFrame !== undefined) {
      cancelAnimationFrame(this.facadeTransitionAnimationFrame);
      this.facadeTransitionAnimationFrame = undefined;
    }

    const startProgress = this.facadeTransitionProgress;
    const distance = Math.abs(targetProgress - startProgress);
    if (distance === 0) {
      this.completeFacadeTransition(targetProgress);
      return;
    }

    const startedAt = performance.now();
    this.pitchTransitionActive = true;
    const tick = (timestamp: number) => {
      const elapsedMs = timestamp - startedAt;
      // Same 250ms ease as Shift+O/T/Z, even for a short 85° to 90° stretch.
      const progress = getFacadeSettleProgress({
        elapsedMs,
        startProgress,
        targetProgress,
      });
      this.applyFacadeTransitionProgress(progress);

      if (getQuickCameraAnimationProgress(elapsedMs) < 1) {
        this.facadeTransitionAnimationFrame = requestAnimationFrame(tick);
        return;
      }

      this.facadeTransitionAnimationFrame = undefined;
      this.completeFacadeTransition(targetProgress);
    };
    this.facadeTransitionAnimationFrame = requestAnimationFrame(tick);
  }

  private applyFacadeTransitionProgress(progress: number): void {
    this.facadeTransitionProgress = progress;
    this.threeJs?.setFacadeTransitionProgress(progress);
    this.applyVerticalFov(
      DEFAULT_MAP_FOV + (ISOMETRIC_MAP_FOV - DEFAULT_MAP_FOV) * progress,
    );
  }

  private completeFacadeTransition(targetProgress: 0 | 1): void {
    this.applyFacadeTransitionProgress(targetProgress);
    this.pitchTransitionActive = false;
    this.syncVerticalFovForPitch();
    this.mapInstance?.triggerRepaint();
    this.notifyCameraChange();
  }

  /** Move the orthographic facade in screen space without changing geography. */
  private panFacadeViewVertically(deltaYPixels: number): void {
    if (!this.facadeModeActive || deltaYPixels === 0) return;
    this.facadeVerticalOffsetPixels += deltaYPixels;
    this.threeJs?.setFacadeVerticalOffsetPixels(
      this.facadeVerticalOffsetPixels,
    );
  }

  private applyVerticalFov(fov: number): void {
    const map = this.mapInstance;
    if (!map) return;
    if (this.appliedVerticalFov === fov) return;
    // Cache before the Mapbox call because camera events may fire re-entrantly.
    this.appliedVerticalFov = fov;
    this.verticalFovUpdateActive = true;
    try {
      map.setVerticalFieldOfView(fov);
    } finally {
      this.verticalFovUpdateActive = false;
    }
  }

  setShowBackgroundMap(show: boolean): void {
    this.showBackgroundMap = show;
    const map = this.mapInstance;
    // Defer application until attach() completes when called before the map
    // is loaded; attach() re-applies the stored value on init.
    if (!map || !this.loaded) return;
    this.syncLocationPinOverlays();
  }

  setShowNearbyBuildings(show: boolean): void {
    this.showNearbyBuildings = show;
    this.renderer?.setShowNearbyBuildings(show);
    const map = this.mapInstance;
    if (!map || !this.loaded) return;
    this.syncLocationPinOverlays();
  }

  /**
   * Show or update the plan underlay image on the map.
   */
  setBlueprintUnderlay(underlay: BlueprintUnderlay | undefined): void {
    this.blueprintUnderlay.setUnderlay(underlay);
    this.syncBlueprintUnderlayPresentation();
  }

  /**
   * When a PDF underlay is visible: place it at its mapped storey start level
   * (Storey dropdown), not the currently selected floor. Keep all storey
   * outlines; hide solid fills below the plan plane when elevated. Soft grey
   * guides for floors below apply only in 2D (PDF contrast).
   */
  private syncBlueprintUnderlayPresentation(): void {
    this.threeJs?.setMode('all-storeys');
    this.threeJs?.setSoftenGuidesBelowSelected(this.perspectiveMode === '2d');

    const underlay = this.blueprintUnderlay.getUnderlay();
    const active = Boolean(underlay?.visible) && !this.facadeModeActive;
    const storeys = this.state.storeys$.getValue();
    const elevation = underlay
      ? getBlueprintUnderlayDisplayElevationMeters(underlay, storeys)
      : 0;
    this.blueprintUnderlay.setElevationMeters(elevation);
    // Skip fills for storeys below the plan; mapped floor + above keep solids.
    this.threeJs?.setHideFillsBelowElevationMeters(
      active ? elevation : undefined,
    );
  }

  /**
   * Hide everything but the plan while measuring its scale: the building
   * overlay and the basemap would otherwise sit on top of the drawing the user
   * is trying to click two points on. Restores the Show-menu state on exit.
   */
  setBlueprintMeasureIsolation(active: boolean): void {
    const map = this.mapInstance;
    if (!map || !this.loaded) return;
    if (this.measureIsolationActive === active) {
      if (active) {
        this.hideMeasureIsolationLayers(map, {
          appearedDuringIsolation: true,
        });
        this.threeJs?.setMeasureIsolation(true);
      }
      return;
    }
    this.measureIsolationActive = active;
    this.eventHandler?.setBuildingPickingPaused(active);

    if (active) {
      // Snapshot rather than force 'visible' on exit: 2D/3D already hides some
      // of these layers, and restoring blindly would reveal them.
      this.measureIsolationVisibility = {};
      // Capture live visibility before three.js hides its own overlays.
      this.hideMeasureIsolationLayers(map, {
        appearedDuringIsolation: false,
      });
      this.threeJs?.setMeasureIsolation(true);
    } else {
      this.threeJs?.setMeasureIsolation(false);
      this.restoreMeasureIsolationLayers(map);
    }
    // Keep `showBackgroundMap` untouched so the Show menu still owns the flag.
    this.syncLocationPinOverlays();
    map.triggerRepaint();
  }

  /**
   * Hide design overlay layers for PDF scale measure.
   */
  private hideMeasureIsolationLayers(
    map: Map,
    options: { appearedDuringIsolation: boolean },
  ): void {
    for (const layerId of MEASURE_ISOLATION_LAYER_IDS) {
      if (!map.getLayer(layerId)) continue;
      try {
        const captured = captureMeasureIsolationVisibility({
          alreadyCaptured: layerId in this.measureIsolationVisibility,
          current: map.getLayoutProperty(layerId, 'visibility'),
          appearedDuringIsolation: options.appearedDuringIsolation,
        });
        if (captured) this.measureIsolationVisibility[layerId] = captured;
        map.setLayoutProperty(layerId, 'visibility', 'none');
      } catch {
        // Layer may be missing during a style swap.
      }
    }
  }

  /**
   * Restore Show-menu / 2D visibility after PDF scale measure.
   */
  private restoreMeasureIsolationLayers(map: Map): void {
    for (const [layerId, visibility] of Object.entries(
      this.measureIsolationVisibility,
    )) {
      if (!map.getLayer(layerId)) continue;
      try {
        map.setLayoutProperty(layerId, 'visibility', visibility);
      } catch {
        // Layer may be missing during a style swap.
      }
    }
    this.measureIsolationVisibility = {};
  }

  /**
   * Control whether the underlay is locked or selected for transform.
   */
  setBlueprintInteractionMode(mode: BlueprintInteractionMode): void {
    this.blueprintUnderlay.setMode(mode);
  }

  getBlueprintInteractionMode(): BlueprintInteractionMode {
    return this.blueprintUnderlay.getMode();
  }

  /**
   * Sync the Design canvas tool into the event handler (gates draft/select/move).
   */
  setDesignMapTool(tool: DesignMapTool): void {
    this.designMapTool = tool;
    this.eventHandler?.setDesignMapTool(tool);
    this.renderer?.setDesignMapTool(tool);
  }

  /** Preview a complete footprint and finish it on the first map click. */
  setBuildingFootprintTemplate(
    template: BuildingFootprintTemplateGeometry | undefined,
    customDrawingActive = false,
  ): void {
    this.buildingFootprintTemplate = template;
    this.customBuildingFootprintDrawingActive = customDrawingActive;
    this.eventHandler?.setBuildingFootprintTemplate(
      template,
      customDrawingActive,
    );
    this.renderer?.setBuildingFootprintTemplateActive(Boolean(template));
  }

  /** Complete the selected preset in the center of the unobscured map canvas. */
  placeBuildingFootprintTemplateAtViewportCenter(
    rightViewportInsetPx = 0,
  ): void {
    const map = this.mapInstance;
    const template = this.buildingFootprintTemplate;
    if (!map || !template || this.isReadonly || this.isDestroyed) return;
    const canvas = map.getCanvas();
    this.placeBuildingFootprintTemplateAtScreenPoint([
      (getMapViewportLeftInset(map) +
        canvas.clientWidth -
        rightViewportInsetPx) /
        2,
      canvas.clientHeight / 2,
    ]);
  }

  /** Complete a panel preset at a drop position relative to the map canvas. */
  placeBuildingFootprintTemplateAtScreenPoint(screenPoint: Point2D): void {
    const map = this.mapInstance;
    const template = this.buildingFootprintTemplate;
    if (!map || !template || this.isReadonly || this.isDestroyed) return;
    const canvas = map.getCanvas();
    if (
      screenPoint[0] < 0 ||
      screenPoint[1] < 0 ||
      screenPoint[0] > canvas.clientWidth ||
      screenPoint[1] > canvas.clientHeight
    )
      return;
    const coordinate = map.unproject([screenPoint[0], screenPoint[1]]);
    const [east, north] = convertCoordinateToMetricOffset(
      coordinate,
      this.state.location$.getValue(),
    );
    const center: Point2D = [east, -north];
    const drafts = createBuildingFootprintTemplateDrafts(
      template,
      center,
      map.getBearing(),
      0,
    );
    if (!this.applyDraftShapesComplete(drafts, true)) {
      this.onSiblingBuildingOverlap?.();
      return;
    }
  }

  /**
   * Space-pan: grab cursor + suppress geometry hits; does not change the tool.
   */
  private setSpacePanActive(active: boolean): void {
    if (this.spacePanActive === active) return;
    this.spacePanActive = active;
    this.mouseNavigation.setSpacePanActive(active);
    this.eventHandler?.setSpacePanActive(active);
    this.renderer?.setSpacePanActive(active);
    this.blueprintUnderlay.setSpacePanActive(active);
  }

  /**
   * Hold Space to pan without selecting the Hand tool chip.
   */
  private attachSpacePanListeners(): void {
    if (this.spacePanListenersAttached) return;
    window.addEventListener('keydown', this.handleSpacePanKeyDown);
    window.addEventListener('keyup', this.handleSpacePanKeyUp);
    window.addEventListener('blur', this.handleSpacePanBlur);
    this.spacePanListenersAttached = true;
  }

  private detachSpacePanListeners(): void {
    if (!this.spacePanListenersAttached) return;
    window.removeEventListener('keydown', this.handleSpacePanKeyDown);
    window.removeEventListener('keyup', this.handleSpacePanKeyUp);
    window.removeEventListener('blur', this.handleSpacePanBlur);
    this.spacePanListenersAttached = false;
    this.setSpacePanActive(false);
  }

  /**
   * Space pans only when the keystroke is not destined for a focused control
   * and focus still sits within the map, so portalled dialogs and side panels
   * keep native Space behaviour.
   */
  private allowsSpacePan(target: EventTarget | null): boolean {
    return allowsDesignSpacePan(target, this.map?.getContainer());
  }

  private readonly handleSpacePanKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'Space') return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (!this.allowsSpacePan(event.target)) return;
    event.preventDefault();
    if (event.repeat) return;
    this.setSpacePanActive(true);
  };

  private readonly handleSpacePanKeyUp = (event: KeyboardEvent): void => {
    if (event.code !== 'Space') return;
    this.setSpacePanActive(false);
  };

  private readonly handleSpacePanBlur = (): void => {
    this.setSpacePanActive(false);
  };

  /**
   * UI callbacks for draft completion and PDF select/deselect.
   */
  setDesignCanvasHandlers(handlers: {
    onDraftComplete?: () => void;
    onBlueprintSelectRequest?: () => void;
    onBlueprintDeselectRequest?: () => void;
    onBlueprintContextMenuRequest?: (position: {
      top: number;
      left: number;
    }) => void;
  }): void {
    this.onDraftComplete = handlers.onDraftComplete;
    this.onBlueprintSelectRequest = handlers.onBlueprintSelectRequest;
    this.onBlueprintDeselectRequest = handlers.onBlueprintDeselectRequest;
    this.onBlueprintContextMenuRequest = handlers.onBlueprintContextMenuRequest;
    this.eventHandler?.setBlueprintSelectionHandlers({
      hitTest: (event) => this.hitTestBlueprint(event),
      hitTestClientPoint: (clientX, clientY) =>
        this.hitTestBlueprintClientPoint(clientX, clientY),
      onSelectRequest: () => this.onBlueprintSelectRequest?.(),
      onDeselectRequest: () => this.onBlueprintDeselectRequest?.(),
      isSelected: () => this.blueprintUnderlay.getMode() !== 'locked',
      onContextMenuRequest: (position) =>
        this.onBlueprintContextMenuRequest?.(position),
    });
  }

  /**
   * True when the event lands inside the visible PDF underlay.
   */
  hitTestBlueprint(event: MapMouseEvent): boolean {
    const location = this.state.location$.getValue();
    if (!location) return false;
    const [mx, my] = convertCoordinateToMetricOffset(event.lngLat, location);
    return this.blueprintUnderlay.hitTestMetric([mx, -my]);
  }

  /**
   * True when a client (viewport) point lands inside the visible PDF underlay.
   * Used for right-click — Mapbox often swallows `contextmenu` on the canvas.
   */
  hitTestBlueprintClientPoint(clientX: number, clientY: number): boolean {
    const map = this.mapInstance;
    const location = this.state.location$.getValue();
    if (!map || !location) return false;
    const rect = map.getCanvas().getBoundingClientRect();
    const lngLat = map.unproject([clientX - rect.left, clientY - rect.top]);
    const [mx, my] = convertCoordinateToMetricOffset(lngLat, location);
    return this.blueprintUnderlay.hitTestMetric([mx, -my]);
  }

  /**
   * Persist callback when the user finishes a transform drag on the underlay.
   */
  setBlueprintUnderlayChangeHandler(
    handler: ((underlay: BlueprintUnderlay) => void) | undefined,
  ): void {
    this.blueprintUnderlay.setOnChange(handler);
  }

  /**
   * Callback for each calibrate-mode map click (local meters).
   */
  setBlueprintCalibratePointHandler(
    handler: ((point: Point2D) => void) | undefined,
  ): void {
    this.blueprintUnderlay.setOnCalibratePoint(handler);
  }

  /**
   * Sync calibrate endpoints / rubber-band line on the map overlay.
   */
  setBlueprintCalibratePoints(points: Point2D[]): void {
    this.blueprintUnderlay.setCalibratePoints(points);
  }

  /**
   * Live labels for active / committed calibration spans on the map.
   */
  setBlueprintCalibrateLabelsHandler(
    handler: ((labels: BlueprintCalibrateLabel[]) => void) | undefined,
  ): void {
    this.blueprintUnderlay.setOnCalibrateLabels(handler);
  }

  /**
   * Called for stepwise blueprint Escape (`cleared-points` or `exited-mode`).
   */
  setBlueprintEscapeHandler(
    handler: ((result: BlueprintEscapeResult) => void) | undefined,
  ): void {
    this.blueprintUnderlay.setOnEscape(handler);
  }

  /**
   * Apply pin-mode / measure-isolation overlay flags in one step so the
   * basemap and PDF cannot drift.
   */
  private syncLocationPinOverlays(): void {
    const map = this.mapInstance;
    applyLocationPinOverlayFlags(
      resolveLocationPinOverlayFlags({
        locationPinMode: this.state.locationPinMode$.getValue(),
        showBackgroundMap: this.showBackgroundMap,
        measureIsolationActive: this.measureIsolationActive,
      }),
      {
        setDisplaySuppressed: (suppressed) => {
          this.blueprintUnderlay.setDisplaySuppressed(
            suppressed || this.facadeModeActive,
          );
        },
        setBackgroundMapVisible: map
          ? (show) =>
              this.applyBackgroundMapVisibility(
                map,
                show && !this.facadeModeActive,
              )
          : undefined,
      },
    );
  }

  private applyBackgroundMapVisibility(map: Map, show: boolean): void {
    this.backgroundMapCurrentlyVisible = show;
    this.ensureBackgroundOverlay(map);
    this.syncBasemapCoverForPitch();
    applyImportedBasemapVisibility(map, show);

    // Hide Mapbox 3D buildings + shadows with clip (and optionally style
    // config). Turning show3dObjects off reloads the Standard style and can
    // leave a blank canvas; only turn models on when Nearby buildings is on.
    const showBasemapModels = show && this.showNearbyBuildings;
    try {
      const current = map.getConfigProperty('basemap', 'show3dObjects');
      if (showBasemapModels && current !== true) {
        map.setConfigProperty('basemap', 'show3dObjects', true);
      }
    } catch {
      // Config property may be unavailable before style import is ready
    }

    // World clip while basemap is hidden so 3D models cannot poke through the PDF.
    this.renderer?.setHideAllMapboxBuildings(!show);
  }

  /** Fade the complete Mapbox basemap while keeping Nodon overlays unaffected. */
  private syncBasemapCoverForPitch(): void {
    const map = this.mapInstance;
    if (!map?.getLayer(MAPBOX_BASEMAP_MASK_LAYER_ID)) return;
    const opacity = this.backgroundMapCurrentlyVisible
      ? getBasemapDimMaskOpacity(map.getPitch())
      : 1;
    try {
      map.setPaintProperty(
        MAPBOX_BASEMAP_MASK_LAYER_ID,
        'fill-opacity',
        opacity,
      );
      setLayerVisibilityIfChanged(
        map,
        MAPBOX_BASEMAP_MASK_LAYER_ID,
        opacity > 0,
      );
    } catch {
      // Style reloads can briefly remove the layer between pitch frames.
    }
  }

  private ensureBackgroundOverlay(map: Map): void {
    try {
      if (!map.getSource(MAPBOX_BASEMAP_MASK_SOURCE_ID)) {
        map.addSource(MAPBOX_BASEMAP_MASK_SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [-180, -90],
                  [180, -90],
                  [180, 90],
                  [-180, 90],
                  [-180, -90],
                ],
              ],
            },
            properties: {},
          },
        });
      }
      if (!map.getLayer(MAPBOX_BASEMAP_MASK_LAYER_ID)) {
        // Unslotted so the plate sits above Standard `top` layers. Zoom
        // re-merges the import; a slotted fill then falls behind streets.
        map.addLayer({
          id: MAPBOX_BASEMAP_MASK_LAYER_ID,
          type: 'fill',
          source: MAPBOX_BASEMAP_MASK_SOURCE_ID,
          // Light gray (not pure white) so a white PDF sheet still reads as a page.
          paint: {
            'fill-color': DESIGN_CANVAS_PLATE_COLOR,
            'fill-opacity': 1,
          },
          layout: { visibility: 'none' },
        });
        placeBasemapCoverAboveImports(map);
      } else {
        // Standard can re-slot the fill on zoom; unslot without getStyle/moveLayer.
        clearBasemapCoverSlot(map);
        const currentColor = map.getPaintProperty(
          MAPBOX_BASEMAP_MASK_LAYER_ID,
          'fill-color',
        );
        if (currentColor !== DESIGN_CANVAS_PLATE_COLOR) {
          map.setPaintProperty(
            MAPBOX_BASEMAP_MASK_LAYER_ID,
            'fill-color',
            DESIGN_CANVAS_PLATE_COLOR,
          );
        }
      }
    } catch (err) {
      console.warn('Failed to ensure background overlay', err);
    }
  }

  /**
   * Clear drafts and apply pin overlay flags whenever pin mode changes.
   * Must be called after {@link destroy} because that unsubscribes
   * {@link locationPinModeSub}.
   */
  private bindLocationPinModeEffects(): void {
    this.locationPinModeSub?.unsubscribe();
    this.locationPinModeSub = this.state.locationPinMode$.subscribe(
      (isPinMode) => {
        if (isPinMode) {
          this.clearDraftShape();
        }
        this.syncLocationPinOverlays();
      },
    );
  }

  /**
   * Setup automatic perspective switching based on zoom level.
   * Switches to 2D mode when zoom crosses from above 15 to below 15.
   */
  private setupZoomAutoSwitch(): void {
    this.zoomAutoSwitchSub?.unsubscribe();

    const zoom$ = this.events?.pointEvents?.map.zoom$;
    if (!zoom$) return;

    // Auto-switch to 2D only when zoom crosses from >= 15 to < 15
    // Only auto-switch if user hasn't manually set 3D mode
    this.zoomAutoSwitchSub = zoom$
      .pipe(
        pairwise(),
        filter(([prevZoom, currentZoom]) => prevZoom >= 15 && currentZoom < 15),
      )
      .subscribe(() => {
        const currentPitch = this.mapInstance?.getPitch() ?? 0;
        // Only switch if currently in 3D mode (pitch > 0)
        if (currentPitch > 0) {
          this.setPerspectiveMode('2d');
        }
      });
  }

  /**
   * Destroy the current map instance, if any.
   */
  destroy(): void {
    // Persist camera before tearing down so tab switches keep zoom/angle.
    const camera = this.getCameraState();
    if (camera) this.cameraChangeHandler?.(camera);

    this.unregisterInstance();
    this.draftInteractionSub?.unsubscribe();
    this.draftInteractionSub = undefined;
    this.draftClickQueue = Promise.resolve();
    this.persistRevision += 1;
    this.persistEpoch += 1;
    this.isDestroyed = true;
    this.locationDragCommitPending = false;
    this.pendingPinLocation = undefined;
    this.pinLocationCommitSequence += 1;
    this.draftClickEpoch = 0;
    this.locationPinModeSub?.unsubscribe();
    this.locationPinModeSub = undefined;
    this.zoomAutoSwitchSub?.unsubscribe();
    this.zoomAutoSwitchSub = undefined;
    this.vertexInteractionSub?.unsubscribe();
    this.vertexInteractionSub = undefined;
    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();
    this.eventHandler?.dispose();
    this.eventHandler = undefined;
    this.pendingPointDeletion = undefined;
    this.middleMousePan.detach();
    this.middleMousePan.setCursorHandler(undefined);
    this.mouseNavigation.detach();
    this.mouseNavigation.setCursorHandler(undefined);
    this.mouseNavigation.setBearingSnapDirectionsProvider(undefined);
    this.mouseNavigation.setFacadeModeHandler(undefined);
    this.mouseNavigation.setFacadeTransitionProgressHandler(undefined);
    this.mouseNavigation.setFacadeTransitionEndHandler(undefined);
    this.mouseNavigation.setFacadeVerticalPanHandler(undefined);
    this.mouseNavigation.setSpacePanActive(false);
    this.detachSpacePanListeners();
    if (this.mapInstance && this.cameraMoveListenerAttached) {
      this.mapInstance.off('moveend', this.handleCameraMoveEnd);
      this.mapInstance.off('rotate', this.handleMapRotate);
      this.mapInstance.off('pitchstart', this.handleMapPitchStart);
      this.mapInstance.off('pitch', this.handleMapPitch);
      this.mapInstance.off('pitchend', this.handleMapPitchEnd);
    }
    this.cameraMoveListenerAttached = false;
    if (this.facadeTransitionAnimationFrame !== undefined) {
      cancelAnimationFrame(this.facadeTransitionAnimationFrame);
      this.facadeTransitionAnimationFrame = undefined;
    }
    this.appliedVerticalFov = undefined;
    this.pitchTransitionActive = false;
    this.facadeModeActive = false;
    this.facadeTransitionProgress = 0;
    this.verticalFovUpdateActive = false;
    this.facadeVerticalOffsetPixels = 0;
    this.backgroundMapCurrentlyVisible = true;
    this.quickPitchReturnCamera = undefined;
    this.buildingVolumeFocusMarginIndex = 0;
    this.cameraChangeHandler = undefined;
    this.bearingChangeHandler = undefined;
    this.renderer?.destroy();
    this.renderer = undefined;
    this.threeJs?.destroy();
    this.threeJs = undefined;
    this.blueprintUnderlay.detach();
    this.backgroundOverlayListenerAttached = false;
    if (this.mapInstance) {
      this.mapInstance.remove();
    }
    this.mapInstance = undefined;
    this.loaded = false;
    this.onCommit = undefined;
    this.onStoreyNavigate = undefined;
    this.onStoreySelect = undefined;
    this.latestReceivedStoreys = [];
    this.state.storeys$.next([]);
    this.state.originalStoreys$.next([]);
    this.state.draftShape$.next(undefined);
    this.state.selectedStoreyId$.next(undefined);
    this.state.locationPinMode$.next(false);
    this.state.penMode$.next(false);
    this.state.otherBuildings$.next([]);
    this.state.hasBuildingSelection$.next(true);
    this.state.isBuildingOutlineSelected$.next(false);
    this.state.hoveredBuildingId$.next(undefined);
    this.onSelectBuilding = undefined;
    this.onDeselectBuilding = undefined;
    this.onHoverBuilding = undefined;
    this.onSiblingBuildingOverlap = undefined;
    this.snapHandler?.dispose();
    this.snapHandler = undefined;
    this.draftPropagationStoreyIds = undefined;
    this.isDisplayOnlyInstance = false;
  }

  /**
   * Build the {@link PointActionContext} used by vertex helpers.
   *
   * Henrik: keep context construction centralized to avoid drift across handlers.
   */
  private buildPointActionContext(
    eventHandler?: MapEventHandler,
  ): PointActionContext {
    const selectionHandler = eventHandler ?? this.eventHandler;
    return {
      map: this.mapInstance,
      location: this.state.location$.getValue(),
      storeys: this.state.storeys$.getValue(),
      selectedStoreyId: this.state.selectedStoreyId$.getValue(),
      handlers: this.handlers,
      getPositionFromCoordinate: (coordinate) => {
        const [mx, my] = convertCoordinateToMetricOffset(
          coordinate,
          this.state.location$.getValue(),
        );
        return [mx, -my];
      },
      selectPoints: (shape, pointIndexes) => {
        // Henrik: route selection through the active event handler when present.
        selectionHandler?.selectPoints(shape, pointIndexes);
      },
    };
  }

  private async insertPointOnDoubleClick(payload: PointPayload): Promise<void> {
    await this.handlers.point.insert.start(payload);
    await this.handlers.point.insert.end(payload);
  }

  /**
   * Wire map-event-handler streams to {@link Handlers} implementations.
   *
   * Henrik: MapEventHandler stays an event source (RxJS), DesignMap owns side effects + persistence.
   */
  private bindVertexInteractionHandlers(eventHandler: MapEventHandler): void {
    this.vertexInteractionSub?.unsubscribe();
    this.vertexInteractionSub = undefined;

    const pointEvents = eventHandler.pointEvents;
    if (!pointEvents) return;

    const subscription = new Subscription();
    const destroy$ = this.destroy$;

    const buildContext = (): PointActionContext =>
      this.buildPointActionContext(eventHandler);

    subscription.add(
      pointEvents.point.mouse.down$
        .pipe(takeUntil(destroy$))
        .subscribe((payload) => {
          this.mapInstance?.dragPan?.disable?.();
          payload.preventDefault();
          if (payload.isInsertDrag) {
            // Clear shape selection so insert-drag only moves the new point.
            eventHandler.deselectShapesAndPoints();
          } else if (!payload.isLineSegmentDrag) {
            eventHandler.applyPointSelection(payload);
          }
          const handler = payload.isInsertDrag
            ? this.handlers.point.insert.start
            : this.handlers.point.move.start;
          void handler(payload);
        }),
    );

    subscription.add(
      pointEvents.line.mouse.doubleClick$
        .pipe(takeUntil(destroy$))
        .subscribe((payload) => {
          payload.preventDefault();
          void this.insertPointOnDoubleClick(payload);
        }),
    );

    subscription.add(
      pointEvents.point.mouse.doubleClick$
        .pipe(takeUntil(destroy$))
        .subscribe((payload) => {
          payload.preventDefault();
          eventHandler.applyPointSelection(payload);
          const shapeId =
            payload.shapeId ??
            (typeof payload.shape.id === 'string'
              ? payload.shape.id
              : undefined);
          const selections = eventHandler.getSelections();
          const selectedPointIndexes =
            shapeId !== undefined && selections.shapeId === shapeId
              ? selections.pointIndexes
              : undefined;
          const rightAngleTargets = payload.isMetaOrCtrlKeyPressed
            ? resolveSelectedCornerRightAngleTargets(
                selectedPointIndexes,
                payload.index,
              )
            : undefined;
          const angleDegrees = getPolygonCornerAngleDegrees(
            payload.shape.points,
            payload.index,
          );
          if (!shapeId || angleDegrees === undefined) return;
          if (payload.isMetaOrCtrlKeyPressed) {
            void this.squareEligibleCornerAngles(shapeId, rightAngleTargets);
            return;
          }
          const signedAreaTwice = payload.shape.points.reduce(
            (sum, point, index) => {
              const next =
                payload.shape.points[(index + 1) % payload.shape.points.length];
              return next ? sum + point[0] * next[1] - next[0] * point[1] : sum;
            },
            0,
          );
          void this.resizeCornerAngle(
            {
              shapeId,
              pointIndex: payload.index,
              angleDegrees,
              pointsAreClockwise: signedAreaTwice > 0,
            },
            90,
          );
        }),
    );

    subscription.add(
      pointEvents.point.mouse.downMove$
        .pipe(takeUntil(destroy$))
        .subscribe((payload) => {
          const handler = payload.isInsertDrag
            ? this.handlers.point.insert.update
            : this.handlers.point.move.update;
          void handler(payload);
        }),
    );

    subscription.add(
      pointEvents.point.mouse.up$
        .pipe(takeUntil(destroy$))
        .subscribe((payload) => {
          void (async () => {
            // Restore map panning immediately on mouseup; async persistence may finish later.
            const context = buildContext();
            try {
              if (shouldIgnoreQuickPointDrag(payload)) {
                // Roll back transient preview updates for quick click-selections.
                if (this.pointMoveBaseStoreys) {
                  this.state.storeys$.next(this.pointMoveBaseStoreys);
                }
                if (
                  payload.preserveMultiPointSelection &&
                  !payload.isLineSegmentDrag &&
                  !payload.isShiftKeyPressed &&
                  !payload.isDraft
                ) {
                  this.eventHandler?.selectPoints(payload.shape as Shape, [
                    payload.index,
                  ]);
                }
                this.pointMoveBaseStoreys = undefined;
                return;
              }
              if (!payload.isInsertDrag && !payload.isLineSegmentDrag) {
                const removalResult = await tryRemovePointOnRelease(
                  context,
                  payload,
                  this.pendingPointDeletion,
                );
                this.pendingPointDeletion = removalResult.pendingDeletion;
                if (removalResult.removed) {
                  const removedShapeId =
                    payload.shapeId ??
                    (typeof payload.shape.id === 'string'
                      ? payload.shape.id
                      : undefined);
                  if (removedShapeId) {
                    this.selectNextPointAfterRemoval(
                      removedShapeId,
                      payload.index,
                    );
                  } else {
                    eventHandler.deselectShapesAndPoints();
                  }
                  return;
                }
              }

              const handler = payload.isInsertDrag
                ? this.handlers.point.insert.end
                : this.handlers.point.move.end;
              const endHandlerPromise = handler(payload);
              await endHandlerPromise;
            } finally {
              this.mapInstance?.dragPan?.enable?.();
            }
          })();
        }),
    );

    subscription.add(
      pointEvents.point.key.backspace$
        .pipe(takeUntil(destroy$))
        .subscribe((keyboardEvent) => {
          const selections = eventHandler.getSelections();
          const shapeId = selections.shapeId;
          const pointIndexes = selections.pointIndexes;
          // This listener is registered before the Design canvas listener.
          // Cancelling Backspace/Delete here stops that listener from
          // removing the selected PDF page.
          if (
            shouldYieldDeleteToBlueprintUnderlay({
              isBlueprintUnderlaySelected:
                this.blueprintUnderlay.getMode() !== 'locked' &&
                Boolean(this.blueprintUnderlay.getUnderlay()),
              hasSelectedShape: Boolean(shapeId),
            })
          ) {
            return;
          }
          keyboardEvent.preventDefault();

          if (!shapeId) return;

          const storeys = this.state.storeys$.getValue();
          const storeyId =
            selections.storeyId ??
            this.state.selectedStoreyId$.getValue() ??
            storeys.find((storey) =>
              storey.shapes.some((shape) => shape.id === shapeId),
            )?.id;

          if (pointIndexes?.length) {
            const shape = storeys
              .flatMap((storey) => storey.shapes)
              .find((candidate) => candidate.id === shapeId);
            // All-vertex (or cutout near-empty) delete removes the shape; avoids triangle→draft.
            if (
              shape &&
              storeyId &&
              shouldRemoveEntireShapeOnVertexDelete({
                selectedPointIndexes: pointIndexes,
                shapePointCount: shape.points.length,
                isCutout: shape.cutout === true,
              })
            ) {
              void (async () => {
                await this.handlers.shape.remove({ shapeId, storeyId });
                eventHandler.deselectShapesAndPoints();
              })();
              return;
            }

            void (async () => {
              const singleRemovedIndex =
                pointIndexes.length === 1 ? pointIndexes[0] : undefined;
              // Resolve all positions from current state before any removal so indexes stay valid.
              const sorted = [...pointIndexes].sort((a, b) => b - a);
              const positions = sorted
                .map((idx) => this.resolvePointPosition(shapeId, idx))
                .filter((p): p is Point2D => p !== undefined);
              for (const position of positions) {
                await this.handlers.point.removeAt?.({ position });
              }
              if (singleRemovedIndex === undefined) {
                eventHandler.deselectShapesAndPoints();
              } else {
                this.selectNextPointAfterRemoval(shapeId, singleRemovedIndex);
              }
            })();
            return;
          }

          if (!storeyId) return;

          void (async () => {
            await this.handlers.shape.remove({ shapeId, storeyId });
            eventHandler.deselectShapesAndPoints();
          })();
        }),
    );

    subscription.add(
      pointEvents.point.key.tab$
        .pipe(takeUntil(destroy$))
        .subscribe((keyboardEvent) => {
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
          this.onStoreyNavigate?.('tab');
        }),
    );

    subscription.add(
      pointEvents.point.key.storeyIndex$
        .pipe(takeUntil(destroy$))
        .subscribe((keyboardEvent) => {
          keyboardEvent.preventDefault();
          keyboardEvent.stopPropagation();
          const index = parseInt(keyboardEvent.key, 10);
          this.onStoreyNavigate?.(index);
        }),
    );

    subscription.add(
      eventHandler
        .getShapeTranslateEnd$()
        .pipe(takeUntil(destroy$))
        .subscribe((payload) => {
          this.mapInstance?.dragPan?.enable?.();
          void this.handlers.shape.translate.end?.(payload);
        }),
    );

    subscription.add(
      eventHandler
        .getShapeClick$()
        .pipe(takeUntil(destroy$))
        .subscribe(({ shapeId, event }) => {
          // Whole-building clicks are resolved from the 3D volume below, so a
          // flat fill feature cannot select an occluded storey first.
          if (this.state.isBuildingOutlineSelected$.getValue()) return;
          event.preventDefault?.();
          if ('originalEvent' in event) {
            (
              event as MapMouseEvent & {
                originalEvent?: MouseEvent;
              }
            ).originalEvent?.preventDefault?.();
          }
          this.focusMapCanvas();
          if (eventHandler.getSelections().shapeId !== shapeId) {
            // Reset selections so only the clicked shape is active.
            eventHandler.deselectShapesAndPoints();
          }
          eventHandler.selectShapeById(shapeId);
        }),
    );

    subscription.add(
      eventHandler
        .getStoreyVolumeClick$()
        .pipe(takeUntil(destroy$))
        .subscribe(({ storeyId }) => {
          this.selectStorey(storeyId);
          this.onStoreySelect?.(storeyId);
        }),
    );

    subscription.add(
      eventHandler
        .getDeselectClick$()
        .pipe(takeUntil(destroy$))
        .subscribe(() => {
          eventHandler.deselectShapesAndPoints();
        }),
    );

    // Shape dragging: disable map pan while translation payload is active.
    subscription.add(
      eventHandler
        .getShapeTranslation$()
        .pipe(takeUntil(destroy$))
        .subscribe((payload) => {
          if (payload) {
            this.mapInstance?.dragPan?.disable?.();
            return;
          }
          this.mapInstance?.dragPan?.enable?.();
        }),
    );

    this.vertexInteractionSub = subscription;
  }

  private focusMapCanvas(): void {
    const canvas = this.mapInstance?.getCanvas();
    if (!canvas) return;
    if (document.activeElement === canvas) return;
    canvas.focus();
  }

  /**
   * Register this instance. Multiple display-only maps may coexist; only one
   * interactive map is kept—attaching a new interactive instance destroys the prior one.
   */
  private registerInstance(): void {
    if (this.isRegisteredInstance) return;

    if (this.isDisplayOnlyInstance) {
      DesignMap.activeDisplayControllers.add(this);
      this.isRegisteredInstance = true;
      return;
    }

    const previousInteractive = DesignMap.activeInteractiveController;
    if (previousInteractive && previousInteractive !== this) {
      console.warn(
        '[DesignMap] Replacing prior interactive map instance with a new one.',
      );
      previousInteractive.destroy();
    }
    DesignMap.activeInteractiveController = this;
    registerDesignDraftDiscard(() => {
      DesignMap.discardActiveDraft();
    });
    this.isRegisteredInstance = true;
  }

  /**
   * Remove this instance from the static controller registry.
   */
  private unregisterInstance(): void {
    if (!this.isRegisteredInstance) return;
    if (this.isDisplayOnlyInstance) {
      DesignMap.activeDisplayControllers.delete(this);
    } else if (DesignMap.activeInteractiveController === this) {
      DesignMap.activeInteractiveController = undefined;
      registerDesignDraftDiscard(() => {});
    }
    this.isRegisteredInstance = false;
  }
}

const waitForNextRender = (map: mapboxgl.Map): Promise<void> => {
  return new Promise((resolve) => {
    const handle = () => {
      map.off('render', handle);
      resolve();
    };
    map.once('render', handle);
    map.triggerRepaint();
  });
};
